import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import {
  faArchive,
  faClipboardCheck,
  faEnvelopeOpenText,
  faHourglassHalf,
  faTrophy
} from '@fortawesome/free-solid-svg-icons';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ConfirmationRequestForm } from 'Src/views/GenericView/AdminView/Messages/ConfirmationRequestForm/ConfirmationRequestForm';
import { FormSubmission } from 'Src/types/formSchema';
import { DatePicker } from 'Src/components/DatePicker/DatePicker';
import { SearchMultiSelectFilter } from 'Src/components/SearchMultiSelectFilter/SearchMultiSelectFilter';
import { DEFAULT_SKIP, LIMIT } from 'Src/consts/table';
import {
  PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/tournament';
import { formatAgesCompact, resolveAgeNamingFromContext } from 'Src/helpers/ageGroups';
import { getTournamentPublicSiteLink } from 'Src/helpers/link/link';
import { Tournament } from 'Src/models/tournament';
import { SchoolInvite } from 'Src/models/invite';
import { TournamentConfirmationRequestMessage } from 'Src/models/message';
import {
  getSchoolUpcomingTournaments,
  getSchoolUpcomingTournamentsCount,
  getSchoolPastTournaments,
  getSchoolPastTournamentsCount
} from 'Src/helpers/service/admin/tournaments';
import { getSchoolWaitingList, getSchoolWaitingListCount } from 'Src/helpers/service/admin/waitingList';
import { withdrawTournamentBooking } from 'Src/helpers/service/admin/invites';
import { unsubscribeTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { getTournamentEvents } from 'Src/helpers/service/adminSU/tournamentEventsTable';
import {
  getAdminSchoolsInboxInvites,
  getAdminSchoolsInboxInvitesCount,
  getAdminSchoolsArchiveInvites,
  getAdminSchoolsArchiveInvitesCount,
  declineAdminSchoolsInvites
} from 'Src/helpers/service/adminSU/schoolInvitesTable';
import {
  getInboxTournamentConfirmationRequests,
  getInboxTournamentConfirmationRequestsCount,
  getArchiveTournamentConfirmationRequests,
  getArchiveTournamentConfirmationRequestsCount,
  acceptConfirmationRequest,
  rejectConfirmationRequest
} from 'Src/helpers/service/admin/messages';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import {
  PageHelpAction,
  PageHelpDefinition,
  PageHelpFilter,
  PageHelpObservation,
  PageHelpRegistration,
  registerPageHelpDefinition
} from 'Src/helpers/pageHelp/pageHelp';
import { isIndividualTournament } from 'Src/helpers/tournament/tournament';
import { AdditionalInfo } from '../TournamentsUpcoming/TournamentDetails/AdditionalInfo/AdditionalInfo';
import { TournamentSchoolStartListReportForm } from '../Reports/TournamentSchoolStartListReportForm/TournamentSchoolStartListReportForm';
import { TournamentSchoolPlayersReportForm } from '../Reports/TournamentSchoolPlayersReportForm/TournamentSchoolPlayersReportForm';
import { SchoolTournamentScheduleReportForm } from '../Reports/SchoolTournamentScheduleReportForm/SchoolTournamentScheduleReportForm';
import { uploadReport } from 'Src/helpers/service/admin/report';
import { getTournamentCommunicationsInbox } from 'Src/helpers/service/adminSU/tournamentCommunications';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/report';
import Moment from 'moment';
import './TournamentsHub.css';

type TabKey = 'upcoming' | 'waiting' | 'invites' | 'requests' | 'archive';
type ArchiveKind = 'all' | 'tournament' | 'invite' | 'request';
type RowKind = 'tournament' | 'waiting' | 'invite' | 'request';
type Tone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';
type Dir = 'asc' | 'desc';
type ActionKind =
  | 'withdrawTournament'
  | 'withdrawWaiting'
  | 'declineInvite'
  | 'declineRequest'
  | 'blockedWithdraw'
  | 'success'
  | 'error'
  | null;
type PreviewEventsState = 'idle' | 'loading' | 'available' | 'empty' | 'error';
type PreviewParticipationState = 'idle' | 'loading' | 'available' | 'error';
type DrawerTab = 'details' | 'updates';
type TournamentReportModal = 'picker' | 'schedule' | 'startList' | 'players' | null;
type ColKey = 'date' | 'name' | 'sport' | 'ages' | 'venue' | 'received' | 'deadline' | 'queue' | 'kind' | 'status';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  initialTab?: TabKey;
}
interface Row {
  id: string;
  kind: RowKind;
  name: string;
  sport: string;
  tournamentType: string;
  organiser: string;
  venue: string;
  startTime: string;
  deadline?: string;
  receivedAt?: string;
  ages: number[];
  gender: string;
  status: string;
  tone: Tone;
  waitPosition?: number | string;
  publicSiteUrl?: string;
  raw: any;
}
interface Col {
  key: ColKey;
  header: string;
  sortable?: boolean;
  locked?: boolean;
}
interface PreviewParticipationSummary {
  teamsCount: number;
  teamsWithPlayersCount: number;
  playersCount: number;
  firstTeamId: string;
  firstTeamName: string;
  isIndividual: boolean;
}
interface State {
  activeTab: TabKey;
  archiveKind: ArchiveKind;
  rows: Record<TabKey, Row[]>;
  nextTournament: Row | null;
  counts: Record<TabKey, number>;
  archiveCounts: Record<ArchiveKind, number>;
  selectedIds: string[];
  previewId: string | null;
  previewEventsState: PreviewEventsState;
  previewParticipationState: PreviewParticipationState;
  previewParticipation: PreviewParticipationSummary | null;
  drawerTab: DrawerTab;
  isLoading: boolean;
  search: string;
  searchDraft: string;
  sportFilter: string;
  organiserFilter: string;
  statusFilter: string;
  dateFrom: string;
  dateTo: string;
  seasonFilter: string;
  sortKey: ColKey;
  sortDir: Dir;
  hiddenColumns: ColKey[];
  currentPage: number;
  pageSize: number;
  isColumnsMenuOpen: boolean;
  isFilterPanelOpen: boolean;
  actionKind: ActionKind;
  actionRowId: string | null;
  actionMessage: string;
  actionError: string;
  isActionLoading: boolean;
  confirmationRequest: TournamentConfirmationRequestMessage | null;
  isConfirmationRequestDirty: boolean;
  isDiscardConfirmationOpen: boolean;
  reportModal: TournamentReportModal;
  chatUnreadCount: number;
}

const EMPTY_ROWS: Record<TabKey, Row[]> = { upcoming: [], waiting: [], invites: [], requests: [], archive: [] };
const EMPTY_COUNTS: Record<TabKey, number> = { upcoming: 0, waiting: 0, invites: 0, requests: 0, archive: 0 };
const TOURNAMENT_INVITE_WHERE = { kind: 'TournamentSchoolInvite' };
const FILTER_BASE = { where: {}, skip: 0, limit: LIMIT, order: 'startTime ASC' };
const ARCHIVE_FILTER_BASE = { where: {}, skip: 0, limit: LIMIT, order: 'updatedAt DESC' };
const DEFAULT_PAGE_SIZE = LIMIT;
const COLUMN_KEYS: ColKey[] = [
  'date',
  'name',
  'sport',
  'ages',
  'venue',
  'received',
  'deadline',
  'queue',
  'kind',
  'status'
];
const getColumnsViewKey = (tab: TabKey): string => `tournaments.school.${tab}`;
const getStoredHiddenColumns = (user: AppUser, tab: TabKey): ColKey[] =>
  loadHiddenColumns(user, getColumnsViewKey(tab)).filter(
    (field): field is ColKey => COLUMN_KEYS.includes(field as ColKey) && field !== 'name'
  );
const STATUS_META: { [key: string]: { label: string; tone: Tone } } = {
  ENTERED: { label: 'Entered', tone: 'info' },
  CONFIRMED: { label: 'Confirmed', tone: 'success' },
  WAITING: { label: 'Waiting list', tone: 'warning' },
  ACCEPTED: { label: 'Accepted', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  DECLINED: { label: 'Declined', tone: 'danger' },
  OUTDATED: { label: 'Expired', tone: 'neutral' },
  CANCELED: { label: 'Cancelled', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'danger' },
  PREBOOKED: { label: 'Prebooked', tone: 'info' },
  NOT_READY: { label: 'Awaiting reply', tone: 'warning' },
  NOT_SENT: { label: 'Not sent', tone: 'neutral' },
  PENDING: { label: 'Awaiting confirmation', tone: 'warning' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'neutral' },
  COMPLETED: { label: 'Completed', tone: 'neutral' }
};

export class TournamentsHub extends React.Component<Props, State> {
  private filterPanelRef = React.createRef<HTMLDivElement>();
  private pageHelpRegistration: PageHelpRegistration | null = null;
  private filterSnapshot: Pick<
    State,
    'sportFilter' | 'organiserFilter' | 'statusFilter' | 'dateFrom' | 'dateTo' | 'seasonFilter'
  > | null = null;
  constructor(props: Props) {
    super(props);
    const activeTab = props.initialTab || this.getTabFromPath(props.location.pathname);
    const restored = (propz.get(props.location, ['state', 'tournamentsHubState'], {}) || {}) as Partial<State>;
    this.state = {
      activeTab,
      archiveKind: restored.archiveKind || 'all',
      rows: EMPTY_ROWS,
      nextTournament: null,
      counts: EMPTY_COUNTS,
      archiveCounts: { all: 0, tournament: 0, invite: 0, request: 0 },
      selectedIds: [],
      previewId: null,
      previewEventsState: 'idle',
      previewParticipationState: 'idle',
      previewParticipation: null,
      drawerTab: 'details',
      isLoading: false,
      search: restored.search || '',
      searchDraft: restored.searchDraft || restored.search || '',
      sportFilter: restored.sportFilter || '',
      organiserFilter: restored.organiserFilter || '',
      statusFilter: restored.statusFilter || '',
      dateFrom: restored.dateFrom || '',
      dateTo: restored.dateTo || '',
      seasonFilter: restored.seasonFilter || '',
      sortKey: restored.sortKey || 'date',
      sortDir: restored.sortDir || 'asc',
      hiddenColumns: getStoredHiddenColumns(props.user, activeTab),
      currentPage: restored.currentPage || 1,
      pageSize: restored.pageSize || DEFAULT_PAGE_SIZE,
      isColumnsMenuOpen: false,
      isFilterPanelOpen: false,
      actionKind: null,
      actionRowId: null,
      actionMessage: '',
      actionError: '',
      isActionLoading: false,
      confirmationRequest: null,
      isConfirmationRequestDirty: false,
      isDiscardConfirmationOpen: false,
      reportModal: null,
      chatUnreadCount: 0
    };
  }
  componentDidMount() {
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    this.loadData();
    this.loadChatUnreadCount();
    this.pageHelpRegistration = registerPageHelpDefinition(this.getPageHelpDefinition());
  }
  loadChatUnreadCount = () => {
    getTournamentCommunicationsInbox(this.props.user, { unread: true, limit: 100 })
      .then(items => this.setState({ chatUnreadCount: items.filter(item => item.unread !== false).length }))
      .catch(() => undefined);
  };
  componentDidUpdate() {
    if (this.pageHelpRegistration) this.pageHelpRegistration.update(this.getPageHelpDefinition());
  }
  componentWillUnmount() {
    if (this.pageHelpRegistration) this.pageHelpRegistration.unregister();
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
  }
  onDocumentMouseDown = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (this.state.isColumnsMenuOpen && !(target && target.closest('[data-tn-columns-menu]'))) {
      this.setState({ isColumnsMenuOpen: false });
    }
    if (
      this.state.isFilterPanelOpen &&
      !target.closest('.sit-dp__pop') &&
      !target.closest('.sit-cal') &&
      this.filterPanelRef.current &&
      !this.filterPanelRef.current.contains(target)
    ) {
      this.cancelFilters();
    }
  };

  openFilters = () => {
    this.filterSnapshot = {
      sportFilter: this.state.sportFilter,
      organiserFilter: this.state.organiserFilter,
      statusFilter: this.state.statusFilter,
      dateFrom: this.state.dateFrom,
      dateTo: this.state.dateTo,
      seasonFilter: this.state.seasonFilter
    };
    this.setState({ isFilterPanelOpen: true, isColumnsMenuOpen: false });
  };

  cancelFilters = () => {
    if (this.filterSnapshot) {
      this.setState({ ...this.filterSnapshot, isFilterPanelOpen: false, currentPage: 1 });
    } else {
      this.setState({ isFilterPanelOpen: false, currentPage: 1 });
    }
    this.filterSnapshot = null;
  };

  applyFilters = () => {
    this.filterSnapshot = null;
    this.setState({ isFilterPanelOpen: false, currentPage: 1, selectedIds: [], previewId: null });
  };

  clearFilters = () => {
    this.filterSnapshot = null;
    this.setState({
      search: '',
      searchDraft: '',
      sportFilter: '',
      organiserFilter: '',
      statusFilter: '',
      seasonFilter: '',
      dateFrom: '',
      dateTo: '',
      currentPage: 1,
      selectedIds: [],
      previewId: null,
      isFilterPanelOpen: false
    });
  };
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const nextTab = nextProps.initialTab || this.getTabFromPath(nextProps.location.pathname);
    if (nextTab !== this.state.activeTab) {
      this.setState({
        activeTab: nextTab,
        selectedIds: [],
        previewId: null,
        previewEventsState: 'idle',
        previewParticipationState: 'idle',
        previewParticipation: null,
        search: '',
        searchDraft: '',
        sportFilter: '',
        organiserFilter: '',
        statusFilter: '',
        dateFrom: '',
        dateTo: '',
        seasonFilter: '',
        hiddenColumns: getStoredHiddenColumns(this.props.user, nextTab),
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        isColumnsMenuOpen: false,
        isFilterPanelOpen: false,
        actionKind: null,
        actionRowId: null,
        actionMessage: '',
        actionError: '',
        isActionLoading: false
      });
    }
  }
  getTabFromPath(pathname: string): TabKey {
    if (pathname.indexOf('/waitingList') !== -1) return 'waiting';
    if (pathname.indexOf('/invites') !== -1) return 'invites';
    if (pathname.indexOf('/requests') !== -1) return 'requests';
    if (pathname.indexOf('/archive') !== -1) return 'archive';
    return 'upcoming';
  }

  loadData = () => {
    const { user } = this.props;
    this.setState({ isLoading: true });
    this.loadActiveTabPreview();

    const safeCount = (request: Promise<any>) => request.catch(() => 0);
    const safeItems = <T,>(request: Promise<T[]>) => request.catch(() => [] as T[]);
    BPromise.all([
      safeCount(getSchoolUpcomingTournamentsCount(user, {})),
      safeCount(getSchoolWaitingListCount(user, {})),
      safeCount(getAdminSchoolsInboxInvitesCount(user, TOURNAMENT_INVITE_WHERE)),
      safeCount(getInboxTournamentConfirmationRequestsCount(user, {})),
      safeCount(getSchoolPastTournamentsCount(user, {})),
      safeCount(getAdminSchoolsArchiveInvitesCount(user, TOURNAMENT_INVITE_WHERE)),
      safeCount(getArchiveTournamentConfirmationRequestsCount(user, {})),
      safeItems(
        getSchoolUpcomingTournaments(user, {
          ...FILTER_BASE,
          where: {},
          limit: 1,
          summary: true
        })
      )
    ])
      .then(
        ([
          upcomingCount,
          waitingCount,
          invitesCount,
          requestsCount,
          pastCount,
          archivedInvitesCount,
          archivedRequestsCount,
          nextTournaments
        ]) => {
          const counts = {
            upcoming: this.getCount(upcomingCount),
            waiting: this.getCount(waitingCount),
            invites: this.getCount(invitesCount),
            requests: this.getCount(requestsCount),
            archive:
              this.getCount(pastCount) + this.getCount(archivedInvitesCount) + this.getCount(archivedRequestsCount)
          };
          this.setState({
            counts,
            nextTournament:
              Array.isArray(nextTournaments) && nextTournaments.length
                ? this.mapTournament(nextTournaments[0], 'tournament')
                : null,
            archiveCounts: {
              all: counts.archive,
              tournament: this.getCount(pastCount),
              invite: this.getCount(archivedInvitesCount),
              request: this.getCount(archivedRequestsCount)
            }
          });
        }
      )
      .catch(() => this.setState({ isLoading: false }));
  };

  loadActiveTabPreview = (tab: TabKey = this.state.activeTab): void => {
    const { user } = this.props;
    const filter = { ...FILTER_BASE, where: {}, limit: LIMIT, summary: true };
    const archiveFilter = { ...ARCHIVE_FILTER_BASE, where: {}, limit: LIMIT };
    const inviteFilter = { ...ARCHIVE_FILTER_BASE, where: TOURNAMENT_INVITE_WHERE, limit: LIMIT };

    let request: Promise<Row[]>;
    switch (tab) {
      case 'waiting':
        request = getSchoolWaitingList(user, filter).then(items =>
          items.map(item => this.mapTournament(item, 'waiting'))
        );
        break;
      case 'invites':
        request = getAdminSchoolsInboxInvites(user, inviteFilter).then(items =>
          items.filter(item => !this.isInviteOutdated(item)).map(item => this.mapInvite(item))
        );
        break;
      case 'requests':
        request = getInboxTournamentConfirmationRequests(user, archiveFilter).then(items =>
          items.map(item => this.mapRequest(item))
        );
        break;
      case 'archive':
        // Archive is a composite list. A slow legacy source must not block or crash the other two.
        request = Promise.all([
          getSchoolPastTournaments(user, { ...archiveFilter, summary: true }).catch(() => []),
          getAdminSchoolsArchiveInvites(user, inviteFilter).catch(() => []),
          getArchiveTournamentConfirmationRequests(user, archiveFilter).catch(() => [])
        ]).then(([past, invites, requests]) => [
          ...past.map(item => this.mapTournament(item, 'tournament')),
          ...invites.map(item => this.mapInvite(item, true)),
          ...requests.map(item => this.mapRequest(item))
        ]);
        break;
      default:
        request = getSchoolUpcomingTournaments(user, filter).then(items =>
          items.map(item => this.mapTournament(item, 'tournament'))
        );
    }

    request
      .then(rows => {
        if (this.state.activeTab !== tab) return;
        this.setState(state => ({
          rows: { ...state.rows, [tab]: rows },
          isLoading: false
        }));
      })
      .catch(() => undefined);
  };

  getCount(value: any): number {
    return Number(propz.get(value, ['count'], value)) || 0;
  }
  getStatusMeta(status: string): { label: string; tone: Tone } {
    const normalized = String(status || '').toUpperCase();
    return STATUS_META[normalized] || { label: this.formatStatus(normalized), tone: 'neutral' };
  }
  formatStatus(status: string): string {
    return String(status || '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/(^|\s)\S/g, text => text.toUpperCase());
  }
  getSportColor(sport: string): string {
    const hash = String(sport || 'sport')
      .split('')
      .reduce((value, char) => ((value << 5) - value + char.charCodeAt(0)) | 0, 0);
    return `hsl(${Math.abs(hash) % 360}, 58%, 47%)`;
  }
  formatDate(value?: string): string {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? '-'
      : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  getVenue(item: any): string {
    return (
      String(propz.get(item, ['venue', 'placeData', 'name'], '') || '').trim() ||
      String(propz.get(item, ['venue', 'placeName'], '') || '').trim() ||
      String(propz.get(item, ['venue', 'postcode'], '') || '').trim() ||
      ''
    );
  }
  getAges(ages: any[]): number[] {
    return Array.isArray(ages)
      ? ages
          .map(age => Number(age))
          .filter(age => Number.isFinite(age))
          .map(age => Math.trunc(age))
      : [];
  }

  getAgeGenderLabel(row: Row): string {
    const naming = resolveAgeNamingFromContext(this.props.user, row.raw);
    const ages = formatAgesCompact(row.ages, naming);
    const gender = (TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING as any)[row.gender] || row.gender;
    return [ages, gender].filter(Boolean).join(' / ') || '-';
  }

  isInviteOutdated(invite: SchoolInvite): boolean {
    const startTime = propz.get<string>(invite, ['tournament', 'startTime'], '');
    const deadline = propz.get<string>(
      invite,
      ['deadlineForAnswers'],
      propz.get(invite, ['tournament', 'deadlineForEntries'], '')
    );
    const now = Date.now();
    const startDate = startTime ? new Date(startTime) : null;
    const deadlineDate = deadline ? new Date(deadline) : null;
    const isTournamentPast = Boolean(startDate && !Number.isNaN(startDate.getTime()) && startDate.getTime() <= now);
    const isDeadlinePast = Boolean(
      deadlineDate && !Number.isNaN(deadlineDate.getTime()) && deadlineDate.getTime() < now
    );
    return isTournamentPast || isDeadlinePast;
  }

  getSportName(item: any): string {
    const sport = propz.get(item, ['sport'], undefined);
    if (typeof sport === 'string' && sport.trim()) return sport.trim();

    return (
      String(propz.get(item, ['sport', 'name'], '') || '').trim() ||
      String(propz.get(item, ['sportName'], '') || '').trim() ||
      String(propz.get(item, ['sportData', 'name'], '') || '').trim() ||
      String(propz.get(item, ['sports', 0, 'name'], '') || '').trim()
    );
  }

  getPublicSiteUrl(tournament: any): string {
    const domain = String(propz.get(tournament, ['domain'], '') || '').trim();
    const status = String(propz.get(tournament, ['publicSite', 'status'], '') || '').toUpperCase();
    if (!domain || status === PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED) return '';
    return getTournamentPublicSiteLink(tournament);
  }

  mapTournament(item: Tournament, kind: RowKind): Row {
    const meta = this.getStatusMeta(kind === 'waiting' ? 'WAITING' : propz.get(item, ['status'], 'ENTERED'));
    return {
      id: `${kind}:${item.id}`,
      kind,
      name: String(propz.get(item, ['name'], '') || 'Tournament'),
      sport: this.getSportName(item),
      tournamentType: String(propz.get(item, ['type'], '') || ''),
      organiser: String(propz.get(item, ['school', 'name'], '') || ''),
      venue: this.getVenue(item),
      startTime: propz.get(item, ['startTime'], ''),
      ages: this.getAges(propz.get(item, ['ages'], [])),
      gender: String(propz.get(item, ['gender'], '') || ''),
      status: meta.label,
      tone: meta.tone,
      waitPosition: propz.get(item, ['waitingListPosition'], undefined),
      publicSiteUrl: this.getPublicSiteUrl(item),
      raw: item
    };
  }
  mapInvite(item: SchoolInvite, forceOutdated = false): Row {
    const tournament = propz.get(item, ['tournament'], {}),
      meta = this.getStatusMeta(forceOutdated ? 'OUTDATED' : propz.get(item, ['status'], 'NOT_READY'));
    return {
      id: `invite:${item.id}`,
      kind: 'invite',
      name: String(propz.get(tournament, ['name'], '') || 'Tournament invite'),
      sport: this.getSportName(item) || this.getSportName(tournament),
      tournamentType: String(propz.get(tournament, ['type'], '') || ''),
      organiser: String(propz.get(item, ['inviterSchool', 'name'], '') || ''),
      venue: this.getVenue(tournament),
      startTime: propz.get(tournament, ['startTime'], ''),
      receivedAt: propz.get(item, ['createdAt'], propz.get(item, ['statusSetAt'], '')),
      deadline: propz.get(
        item,
        ['deadlineForAnswers'],
        propz.get(tournament, ['deadlineForEntries'], propz.get(tournament, ['deadlineForAnswers'], ''))
      ),
      ages: this.getAges(propz.get(tournament, ['ages'], [])),
      gender: String(propz.get(tournament, ['gender'], '') || ''),
      status: meta.label,
      tone: meta.tone,
      publicSiteUrl: this.getPublicSiteUrl(tournament),
      raw: item
    };
  }
  mapRequest(item: TournamentConfirmationRequestMessage): Row {
    const tournament = propz.get(item, ['tournamentData'], {}),
      meta = this.getStatusMeta(propz.get(item, ['invitationStatus'], 'PENDING'));
    return {
      id: `request:${item.id}`,
      kind: 'request',
      name: String(propz.get(tournament, ['name'], '') || propz.get(item, ['title'], 'Tournament request')),
      sport: this.getSportName(tournament),
      tournamentType: String(propz.get(tournament, ['type'], '') || ''),
      organiser: String(propz.get(item, ['schoolData', 'name'], propz.get(item, ['sender', 'schoolName'], '')) || ''),
      venue: this.getVenue(tournament),
      startTime: propz.get(tournament, ['startTime'], ''),
      deadline: propz.get(item, ['deadlineForAnswers'], ''),
      receivedAt: propz.get(item, ['createdAt'], ''),
      ages: this.getAges(propz.get(tournament, ['ages'], [])),
      gender: String(propz.get(tournament, ['gender'], '') || ''),
      status: meta.label,
      tone: meta.tone,
      publicSiteUrl: this.getPublicSiteUrl(tournament),
      raw: item
    };
  }

  switchTab = (tab: TabKey) => {
    const path =
      tab === 'upcoming' ? '/tournaments/upcoming' : `/tournaments/${tab === 'waiting' ? 'waitingList' : tab}`;
    this.props.history.push(path);
    this.setState(
      {
        activeTab: tab,
        selectedIds: [],
        previewId: null,
        previewEventsState: 'idle',
        previewParticipationState: 'idle',
        previewParticipation: null,
        archiveKind: 'all',
        search: '',
        searchDraft: '',
        sportFilter: '',
        organiserFilter: '',
        statusFilter: '',
        dateFrom: '',
        dateTo: '',
        seasonFilter: '',
        hiddenColumns: getStoredHiddenColumns(this.props.user, tab),
        currentPage: 1,
        isColumnsMenuOpen: false,
        isFilterPanelOpen: false
      },
      () => {
        if (this.state.rows[tab].length === 0) this.loadActiveTabPreview(tab);
      }
    );
  };
  getSourceRows(): Row[] {
    const { rows, activeTab, archiveKind } = this.state;
    const source = rows[activeTab] || [];
    return activeTab === 'archive' && archiveKind !== 'all' ? source.filter(row => row.kind === archiveKind) : source;
  }
  getSeasonFromDate(value?: string): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return String(date.getMonth() >= 8 ? date.getFullYear() : date.getFullYear() - 1);
  }
  isRowInDateRange(row: Row): boolean {
    const { dateFrom, dateTo } = this.state;
    if (!dateFrom && !dateTo) return true;
    const date = new Date(row.startTime);
    if (Number.isNaN(date.getTime())) return false;
    if (dateFrom) {
      const from = new Date(`${dateFrom}T00:00:00`);
      if (date < from) return false;
    }
    if (dateTo) {
      const to = new Date(`${dateTo}T23:59:59`);
      if (date > to) return false;
    }
    return true;
  }
  getFilteredRows(): Row[] {
    const { search, sportFilter, organiserFilter, statusFilter, seasonFilter } = this.state;
    const filtered = this.getSourceRows().filter(row => {
      const haystack = `${row.name} ${row.sport} ${row.organiser} ${row.venue}`.toLowerCase();
      return (
        !(search.trim() && !haystack.includes(search.trim().toLowerCase())) &&
        !(sportFilter && row.sport !== sportFilter) &&
        !(organiserFilter && row.organiser !== organiserFilter) &&
        !(statusFilter && row.status !== statusFilter) &&
        !(seasonFilter && this.getSeasonFromDate(row.startTime) !== seasonFilter) &&
        this.isRowInDateRange(row)
      );
    });
    return this.sortRows(filtered);
  }
  getActiveRows(): Row[] {
    const { currentPage, pageSize } = this.state;
    const start = (currentPage - 1) * pageSize;
    return this.getFilteredRows().slice(start, start + pageSize);
  }
  updateFilter(
    key: 'search' | 'sportFilter' | 'organiserFilter' | 'statusFilter' | 'dateFrom' | 'dateTo' | 'seasonFilter',
    value: string
  ): void {
    this.setState({ [key]: value, currentPage: 1, selectedIds: [], previewId: null } as Pick<
      State,
      | 'search'
      | 'sportFilter'
      | 'organiserFilter'
      | 'statusFilter'
      | 'dateFrom'
      | 'dateTo'
      | 'seasonFilter'
      | 'currentPage'
      | 'selectedIds'
      | 'previewId'
    >);
  }
  parseFilterDate(value: string): Date | null {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  formatFilterDate(value: any): string {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad2 = (n: number) => (String(n).length >= 2 ? String(n) : `0${n}`);
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
  }
  onTournamentDateRangeChange = (value: any): void => {
    const start = value && value.start ? this.formatFilterDate(value.start) : '';
    const end = value && value.end ? this.formatFilterDate(value.end) : '';
    this.setState({ dateFrom: start, dateTo: end, currentPage: 1, selectedIds: [], previewId: null });
  };
  clearFilter = (
    key: 'search' | 'sportFilter' | 'organiserFilter' | 'statusFilter' | 'seasonFilter' | 'dateRange'
  ): void => {
    if (key === 'search') {
      this.setState({ search: '', searchDraft: '', currentPage: 1, selectedIds: [], previewId: null });
      return;
    }
    if (key === 'dateRange') {
      this.setState({ dateFrom: '', dateTo: '', currentPage: 1, selectedIds: [], previewId: null });
      return;
    }
    this.updateFilter(key, '');
  };
  sortRows(rows: Row[]): Row[] {
    const { sortKey, sortDir } = this.state;
    return [...rows].sort((a, b) => {
      const result = String(this.getSortValue(a, sortKey)).localeCompare(
        String(this.getSortValue(b, sortKey)),
        undefined,
        { numeric: true }
      );
      return sortDir === 'asc' ? result : -result;
    });
  }
  getSortValue(row: Row, key: ColKey): string | number {
    switch (key) {
      case 'date':
        return row.startTime || '';
      case 'received':
        return row.receivedAt || '';
      case 'deadline':
        return row.deadline || '';
      case 'queue':
        return Number(row.waitPosition) || 999;
      case 'kind':
        return row.kind;
      case 'ages':
        return this.getAgeGenderLabel(row);
      case 'venue':
        return row.venue;
      case 'status':
        return row.status;
      case 'sport':
        return row.sport;
      default:
        return row.name;
    }
  }
  onSort = (key: ColKey) =>
    this.setState(state => ({
      sortKey: key,
      sortDir: state.sortKey === key && state.sortDir === 'asc' ? 'desc' : 'asc'
    }));
  getPreviewRow(): Row | undefined {
    const allRows = Object.keys(this.state.rows).reduce((acc, key) => acc.concat(this.state.rows[key]), [] as Row[]);
    return (
      allRows.find(row => row.id === this.state.previewId) ||
      (this.state.nextTournament && this.state.nextTournament.id === this.state.previewId
        ? this.state.nextTournament
        : undefined)
    );
  }
  openPreview = (row: Row) => {
    if (row.kind === 'tournament') {
      const snapshot: any = {};
      ['search', 'searchDraft', 'sportFilter', 'organiserFilter', 'statusFilter', 'dateFrom', 'dateTo', 'seasonFilter', 'sortKey', 'sortDir', 'currentPage', 'pageSize', 'archiveKind'].forEach(key => {snapshot[key] = this.state[key];});
      this.props.history.push({pathname: '/tournaments/participation/overview', search: `?tournamentId=${this.getTournamentId(row)}`,
        state: {participationReturnPath: this.props.location.pathname, participationReturnSearch: this.props.location.search,
          participationListState: {...(this.props.location.state as any), tournamentsHubState: snapshot}}});
      return;
    }
    this.setState({
      previewId: row.id,
      previewEventsState: 'idle',
      previewParticipationState: 'idle',
      previewParticipation: null,
      drawerTab: 'details'
    });
  };
  closePreview = () =>
    this.setState({
      previewId: null,
      previewEventsState: 'idle',
      previewParticipationState: 'idle',
      previewParticipation: null,
      drawerTab: 'details'
    });

  getPageHelpFilters(): PageHelpFilter[] {
    const { search, sportFilter, organiserFilter, statusFilter, seasonFilter, dateFrom, dateTo } = this.state;
    const filters: PageHelpFilter[] = [];
    if (search.trim()) filters.push({ id: 'search', label: 'Search', value: search.trim() });
    if (seasonFilter) filters.push({ id: 'season', label: 'Season', value: seasonFilter });
    if (dateFrom || dateTo) {
      filters.push({
        id: 'date',
        label: 'Tournament date',
        value: [dateFrom || 'Any start', dateTo || 'Any end'].join(' – ')
      });
    }
    if (sportFilter) filters.push({ id: 'sport', label: 'Sport', value: sportFilter });
    if (organiserFilter) filters.push({ id: 'organiser', label: 'Organising union', value: organiserFilter });
    if (statusFilter) filters.push({ id: 'status', label: 'Status', value: statusFilter });
    return filters;
  }

  getEmptyHelpText(tab: TabKey): string {
    switch (tab) {
      case 'waiting':
        return 'Your school is not currently queued for a tournament place. Accepted waiting-list bookings appear here with their queue position.';
      case 'invites':
        return 'There are no tournament invitations waiting for a response. New organiser invitations will appear here.';
      case 'requests':
        return 'There are no tournament confirmation requests waiting for a response. These requests concern tournaments your school has already booked.';
      case 'archive':
        return 'There are no past tournaments or resolved tournament responses in this archive view.';
      default:
        return 'There are no future tournaments in which your school is currently entered or confirmed.';
    }
  }

  getEmptyTitle(tab: TabKey): string {
    switch (tab) {
      case 'waiting':
        return 'No waiting-list places';
      case 'invites':
        return 'No tournament invitations';
      case 'requests':
        return 'No confirmation requests';
      case 'archive':
        return 'No archived tournaments or responses';
      default:
        return 'No upcoming tournaments';
    }
  }

  getEmptyIcon(tab: TabKey): any {
    switch (tab) {
      case 'waiting':
        return faHourglassHalf;
      case 'invites':
        return faEnvelopeOpenText;
      case 'requests':
        return faClipboardCheck;
      case 'archive':
        return faArchive;
      default:
        return faTrophy;
    }
  }

  getTabHelpItems(tab: TabKey): string[] {
    switch (tab) {
      case 'waiting':
        return [
          'Each row is a tournament where your school has accepted a waiting-list place but has not yet been admitted as a participant.',
          'Open a row to see the current queue position and tournament details.',
          'Withdraw only when the school no longer wants the place. If the organiser admits your school, the tournament moves to Upcoming.'
        ];
      case 'invites':
        return [
          'An invite is an organiser’s offer to book your school into a tournament.',
          'Open one row to review the tournament, response deadline and Accept or Decline actions.',
          'Accepting can confirm a place or put the school onto the waiting list, depending on the tournament’s current capacity.'
        ];
      case 'requests':
        return [
          'A confirmation request is not a new invite: your school is already booked and the organiser is asking you to reconfirm participation.',
          'Accept opens the organiser’s form. Complete its required answers before submitting.',
          'Declining tells the organiser that the school no longer intends to participate and may release the place.'
        ];
      case 'archive':
        return [
          'Use Tournaments, Invites and Requests to narrow the combined archive.',
          'Archived invites and requests are resolved or expired and no longer offer response actions.',
          'Past tournament rows remain useful for reviewing teams, events and the public tournament page where available.'
        ];
      default:
        return [
          'Upcoming contains future tournaments where your school is entered or confirmed as a participant.',
          'Open a tournament to check the school’s team and participant preparation before the tournament starts.',
          'Manage teams first. For individual tournaments, add children to the single school team and then allocate them to the organiser’s events.',
          'Manage events becomes available after the organiser has created tournament events for your school.'
        ];
    }
  }

  scrollToHelpAnchor = (anchorId: string): void => {
    const element = document.querySelector(`[data-help-anchor="${anchorId}"]`) as HTMLElement | null;
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    const control = (element.querySelector('input') ||
      element.querySelector('select') ||
      element.querySelector('button')) as HTMLElement | null;
    if (control) window.setTimeout(() => control.focus(), 320);
  };

  getPageHelpDefinition(): PageHelpDefinition {
    const { activeTab, counts, selectedIds, isLoading, currentPage, pageSize } = this.state;
    const previewRow = this.getPreviewRow();
    const filters = this.getPageHelpFilters();
    const filteredRows = this.getFilteredRows();
    const unfilteredActiveCount =
      activeTab === 'archive' ? this.state.archiveCounts[this.state.archiveKind] : counts[activeTab];
    const activeCount = filters.length > 0 ? filteredRows.length : unfilteredActiveCount;
    const displayedPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const tabLabel = this.getTabTitle();
    const participation = this.state.previewParticipation;
    const observations: PageHelpObservation[] = [
      {
        id: 'tournament-active-view',
        tone: activeCount > 0 ? 'positive' : 'neutral',
        title: `${tabLabel} · ${activeCount.toLocaleString('en-GB')}`,
        text:
          activeCount > 0
            ? `${this.getTabs().find(tab => tab.key === activeTab)?.subtitle ||
                ''} ${activeTab === 'upcoming' || activeTab === 'archive' ? 'Open a tournament to manage your participation in its full workspace.' : 'Click a row to review its details and available actions.'}`
            : this.getEmptyHelpText(activeTab)
      }
    ];

    if (filters.length > 0) {
      observations.push({
        id: 'tournament-filter-state',
        tone: filteredRows.length > 0 ? 'neutral' : 'warning',
        title: `${filters.length} active ${filters.length === 1 ? 'filter' : 'filters'}`,
        text:
          filteredRows.length > 0
            ? `${filteredRows.length.toLocaleString('en-GB')} ${
                filteredRows.length === 1 ? 'row matches' : 'rows match'
              } the current search and filters.`
            : 'No records shown match all current criteria. Remove one filter or use Clear all to widen the list.'
      });
    }

    if (!previewRow && selectedIds.length > 0 && (activeTab === 'invites' || activeTab === 'requests')) {
      observations.push({
        id: 'tournament-selection-state',
        tone: selectedIds.length === 1 ? 'positive' : activeTab === 'invites' ? 'warning' : 'neutral',
        title: `${selectedIds.length} ${selectedIds.length === 1 ? 'record selected' : 'records selected'}`,
        text:
          activeTab === 'invites'
            ? selectedIds.length === 1
              ? 'Review invite is available. Clicking the row instead opens the same invite’s details drawer.'
              : 'Invites must be reviewed one at a time, so Review invite is unavailable until only one remains selected.'
            : selectedIds.length === 1
            ? 'Review requests is available. You can also click the row to open this request directly.'
            : 'Review requests opens the confirmation-request workspace. Each request still needs its own answers and response.'
      });
    }

    if (previewRow) {
      if (previewRow.kind === 'tournament') {
        if (this.state.previewParticipationState === 'loading') {
          observations.push({
            id: 'tournament-preparation-loading',
            tone: 'neutral',
            title: 'Checking school preparation',
            text: 'Squady is checking teams and children for this tournament.'
          });
        } else if (this.state.previewParticipationState === 'error') {
          observations.push({
            id: 'tournament-preparation-error',
            tone: 'warning',
            title: 'Team preparation could not be checked',
            text: 'You can still open Manage teams and review the school’s tournament setup directly.'
          });
        } else if (participation) {
          const isNoTeam = participation.teamsCount === 0;
          const isNoPlayers = participation.playersCount === 0;
          observations.push({
            id: 'tournament-preparation',
            tone: activeTab === 'archive' ? 'neutral' : isNoTeam || isNoPlayers ? 'warning' : 'positive',
            title:
              activeTab === 'archive'
                ? `${participation.teamsCount} ${participation.teamsCount === 1 ? 'team' : 'teams'} · ${
                    participation.playersCount
                  } ${participation.playersCount === 1 ? 'child' : 'children'}`
                : isNoTeam
                ? 'No school team added yet'
                : `${participation.teamsCount} ${participation.teamsCount === 1 ? 'team' : 'teams'} · ${
                    participation.playersCount
                  } ${participation.playersCount === 1 ? 'child' : 'children'}`,
            text:
              activeTab === 'archive'
                ? 'This is the team and participant record retained for the past tournament. Open it for historical review.'
                : isNoTeam
                ? participation.isIndividual
                  ? 'Create the school’s single tournament team first, then add children and allocate them to tournament events.'
                  : 'Create the required school team or teams before adding children.'
                : isNoPlayers
                ? 'The team structure exists, but no children have been added. Open the team’s participants before working with event allocations.'
                : participation.isIndividual
                ? 'Children are in the individual tournament team. Review Allocate to events once the organiser’s events are available.'
                : `${participation.teamsWithPlayersCount} of ${participation.teamsCount} ${
                    participation.teamsCount === 1 ? 'team has' : 'teams have'
                  } children added. Review each squad before the tournament.`
          });
        }

        observations.push({
          id: 'tournament-events-state',
          tone: this.state.previewEventsState === 'available' ? 'positive' : 'neutral',
          title:
            this.state.previewEventsState === 'loading'
              ? 'Checking tournament events'
              : this.state.previewEventsState === 'available'
              ? 'Tournament events are available'
              : this.state.previewEventsState === 'empty'
              ? 'The organiser has not created events yet'
              : this.state.previewEventsState === 'error'
              ? 'Tournament events could not be checked'
              : 'Tournament events',
          text:
            this.state.previewEventsState === 'available'
              ? activeTab === 'archive'
                ? 'Past tournament events are available for historical review.'
                : participation && participation.isIndividual
                ? 'Manage events is available. Allocate children from the tournament team to the appropriate events.'
                : 'Manage events is available for reviewing the organiser’s fixtures and your school’s involvement.'
              : this.state.previewEventsState === 'empty'
              ? 'Prepare teams and children now; Manage events will unlock after the organiser publishes tournament events.'
              : this.state.previewEventsState === 'error'
              ? 'Close and reopen the tournament details to retry the event check.'
              : 'Squady is checking whether the organiser has created events for your school.'
        });
      } else if (previewRow.kind === 'waiting') {
        observations.push({
          id: 'waiting-position',
          tone: 'warning',
          title: previewRow.waitPosition ? `Queue position #${previewRow.waitPosition}` : 'Waiting for a place',
          text:
            'The school is not yet a tournament participant. The organiser can move it into Upcoming when a place becomes available.'
        });
      } else {
        observations.push({
          id: 'tournament-response',
          tone: this.isDeadlineSoon(previewRow.deadline) ? 'warning' : 'neutral',
          title: previewRow.kind === 'invite' ? 'Tournament invite awaiting response' : 'Participation confirmation',
          text: previewRow.deadline
            ? `Respond by ${this.formatDate(previewRow.deadline)}${
                this.getDaysLeftText(previewRow.deadline) ? ` · ${this.getDaysLeftText(previewRow.deadline)}` : ''
              }.`
            : 'No response deadline is shown. Review the tournament details before accepting or declining.'
        });
      }
    }

    const actions: PageHelpAction[] = previewRow
      ? []
      : [
          {
            id: 'tournaments-open-filters',
            label: 'Open tournament filters',
            description: 'Narrow this section by season, date, sport, organiser or status.',
            anchorId: 'tournaments-filters',
            run: this.openFilters
          },
          ...(filters.length > 0
            ? [
                {
                  id: 'tournaments-clear-filters',
                  label: 'Clear all filters',
                  description: 'Remove the search term and every active filter in this tournament section.',
                  run: this.clearFilters
                }
              ]
            : [])
        ];

    if (!previewRow && activeTab === 'upcoming' && this.state.nextTournament) {
      const nextTournament = this.state.nextTournament;
      actions.push({
        id: 'tournaments-review-next',
        label: 'Review the next tournament',
        description: `Open ${nextTournament.name} and check teams, children and event availability.`,
        kind: 'primary',
        anchorId: 'tournaments-results',
        run: () => this.openPreview(nextTournament)
      });
    }

    if (!previewRow && selectedIds.length === 1 && (activeTab === 'invites' || activeTab === 'requests')) {
      const selectedRow = this.state.rows[activeTab].find(row => row.id === selectedIds[0]);
      if (selectedRow) {
        actions.push({
          id: 'tournaments-review-selected-response',
          label: activeTab === 'invites' ? 'Review selected invite' : 'Review selected request',
          description: 'Open this selected record’s details and response actions.',
          kind: 'primary',
          anchorId: 'tournaments-selection',
          run: () => this.openPreview(selectedRow)
        });
      }
    }

    if (previewRow) {
      if (previewRow.kind === 'tournament') {
        const canOpenFirstTeam = Boolean(participation && participation.firstTeamId);
        const participationAction =
          participation && participation.teamsCount === 0
            ? {
                label: participation.isIndividual ? 'Create the tournament team' : 'Add tournament teams',
                description: participation.isIndividual
                  ? 'Create the single school team used to hold every child in this individual tournament.'
                  : 'Create the school team or teams required for this tournament.',
                run: () => this.openTeams(previewRow)
              }
            : participation && participation.playersCount === 0 && canOpenFirstTeam
            ? {
                label: `Add children${participation.firstTeamName ? ` to ${participation.firstTeamName}` : ''}`,
                description: 'Open the team participant editor and add eligible children before allocating events.',
                run: () => this.openTeamPlayersFromHelp(previewRow, 'manage')
              }
            : participation && participation.isIndividual && canOpenFirstTeam
            ? {
                label: 'Review children & allocate to events',
                description: 'Open the individual tournament team to review children and their event allocations.',
                run: () => this.openTeamPlayersFromHelp(previewRow, 'allocate')
              }
            : {
                label: 'Manage tournament teams',
                description: 'Review team setup and the children added to each squad.',
                run: () => this.openTeams(previewRow)
              };
        actions.push({
          id: 'tournaments-manage-participation',
          ...participationAction,
          kind: 'primary',
          anchorId: 'tournaments-drawer-actions',
          disabled: this.state.previewParticipationState === 'loading'
        });
        if (this.state.previewEventsState === 'available') {
          actions.push({
            id: 'tournaments-manage-events',
            label:
              participation && participation.isIndividual
                ? 'Open tournament events'
                : 'Manage tournament events',
            description:
              participation && participation.isIndividual
                ? 'Review the available events and the children allocated to them.'
                : 'Review the organiser’s tournament events involving your school.',
            anchorId: 'tournaments-drawer-actions',
            run: () => this.openTournament(previewRow)
          });
        }
      } else if (previewRow.kind === 'invite' && activeTab !== 'archive') {
        actions.push({
          id: 'tournaments-accept-invite',
          label: 'Review and accept invite',
          description: 'Open the booking flow. No acceptance is submitted until you complete and confirm it.',
          kind: 'primary',
          anchorId: 'tournaments-drawer-actions',
          run: () => this.openAcceptInvite(previewRow)
        });
      } else if (previewRow.kind === 'request' && activeTab !== 'archive') {
        actions.push({
          id: 'tournaments-answer-request',
          label: 'Complete confirmation request',
          description: 'Open the organiser’s confirmation form and review all required answers.',
          kind: 'primary',
          anchorId: 'tournaments-drawer-actions',
          run: () => this.openAcceptRequest(previewRow)
        });
      } else if (previewRow.kind === 'waiting') {
        actions.push({
          id: 'tournaments-review-waiting-action',
          label: 'Review waiting-list action',
          description: 'Go to the withdrawal button. Squady will not run this destructive action for you.',
          anchorId: 'tournaments-drawer-actions',
          run: () => this.scrollToHelpAnchor('tournaments-drawer-actions')
        });
      }
      actions.push({
        id: 'tournaments-close-details',
        label: 'Close tournament details',
        description: 'Return to the current tournament list without changing anything.',
        run: this.closePreview
      });
    }

    return {
      screenId: previewRow
        ? `school.tournaments.${activeTab}.${previewRow.kind}.details`
        : `school.tournaments.${activeTab}`,
      title: previewRow ? previewRow.name : tabLabel,
      subtitle: previewRow
        ? 'Tournament details, school preparation and the actions currently available.'
        : 'Find tournament work and understand the next safe action for your school.',
      entity: { singular: 'tournament record', plural: 'tournament records' },
      useDefaultSelectionObservation: false,
      state: {
        loading: isLoading,
        visibleCount: this.getActiveRows().length,
        selectedCount: selectedIds.length,
        currentPage,
        totalPages: displayedPages,
        activeView: tabLabel,
        filters
      },
      observations,
      actions,
      anchors: [
        {
          id: 'tournaments-tabs',
          label: 'Tournament sections',
          description:
            'Move between Upcoming, Waiting list, Invites, confirmation Requests and Archive. Each tab count belongs only to that section.'
        },
        {
          id: 'tournaments-summary',
          label: 'Tournament overview',
          description: 'These compact counters link to the next tournament and sections that may need a response.'
        },
        {
          id: 'tournaments-search',
          label: 'Search tournaments',
          description:
            'Enter part of a tournament, sport, organiser or venue name. Clear the field with its cross when finished.'
        },
        {
          id: 'tournaments-filters',
          label: 'Filters',
          description:
            'Combine season, tournament date, sport, organising union and status, then choose Apply or press Enter.'
        },
        {
          id: 'tournaments-columns',
          label: 'Columns',
          description:
            'Choose the information shown in this tab. Tournament name remains locked and preferences are saved for this user.'
        },
        ...(activeTab === 'archive'
          ? [
              {
                id: 'tournaments-archive-kinds',
                label: 'Archive type',
                description:
                  'Show all archive records together or narrow the view to past tournaments, resolved invites or resolved requests.'
              }
            ]
          : []),
        ...(activeTab === 'invites' || activeTab === 'requests'
          ? [
              {
                id: 'tournaments-selection',
                label: 'Select response records',
                description:
                  selectedIds.length > 0
                    ? `${selectedIds.length} selected. Use the selection bar to review them, or Clear to remove the selection.`
                    : 'Use checkboxes for selection actions. Click the row itself when you want to open one record’s details.'
              }
            ]
          : []),
        {
          id: 'tournaments-results',
          label: tabLabel,
          description:
            activeTab === 'upcoming'
              ? 'Click a tournament to inspect team, child and event preparation for your school.'
              : activeTab === 'waiting'
              ? 'Click a row to review queue position and the option to withdraw.'
              : activeTab === 'archive'
              ? 'Click a row to review historical details. Response and withdrawal actions are no longer available.'
              : 'Click a row to review its deadline, tournament details and response actions.'
        },
        {
          id: 'tournaments-pagination',
          label: 'Pagination',
          description:
            displayedPages <= 1
              ? 'All rows currently displayed in this view fit on one page.'
              : `This view has ${displayedPages} displayed pages. Use Previous and Next to move between them.`
        },
        ...(previewRow
          ? [
              {
                id: 'tournaments-drawer',
                label: `${previewRow.name} details`,
                description:
                  'Review status, organiser, date, venue, age groups, gender and the public tournament website where available.'
              },
              {
                id: 'tournaments-drawer-actions',
                label: 'Available tournament actions',
                description:
                  activeTab === 'archive'
                    ? 'This archived record has no response or withdrawal actions; past teams and events remain available for review.'
                    : previewRow.kind === 'tournament'
                    ? 'Prepare teams and children first, then use tournament events when the organiser has created them.'
                    : previewRow.kind === 'waiting'
                    ? 'Withdrawal removes the school from the queue and requires confirmation.'
                    : 'Accept continues to a review flow. Decline is destructive and asks for confirmation.'
              }
            ]
          : [])
      ],
      sections: [
        {
          id: 'tournaments-current-section',
          title: `How ${tabLabel} works`,
          items: this.getTabHelpItems(activeTab),
          anchorIds: ['tournaments-tabs', 'tournaments-results']
        },
        ...(previewRow && previewRow.kind === 'tournament'
          ? [
              {
                id: 'tournament-preparation-flow',
                title:
                  activeTab === 'archive'
                    ? 'Past tournament participation'
                    : participation && participation.isIndividual
                    ? 'Individual tournament preparation'
                    : 'Team preparation',
                items:
                  activeTab === 'archive'
                    ? [
                        'Team, participant and event records are retained for historical review.',
                        'Archive does not offer response or withdrawal actions.',
                        'Use View teams or View events to inspect what was recorded for the tournament.'
                      ]
                    : participation && participation.isIndividual
                    ? [
                        'An individual tournament still uses one school tournament team as the container for your entered children.',
                        'Create that team, add eligible children, then use Allocate to events to place each child into the organiser’s event programme.',
                        'Manage events is enabled only after tournament events exist. Event allocation can be reviewed again as the organiser updates the programme.'
                      ]
                    : [
                        'Create every required team and add the children who will represent the school.',
                        'Review each team’s age and gender against the tournament entry rules.',
                        'When tournament events exist, use Manage events to review fixtures and school participation.'
                      ],
                anchorIds: ['tournaments-drawer-actions']
              }
            ]
          : []),
        {
          id: 'tournaments-row-versus-selection',
          title: 'Opening and selecting are different',
          items: [
            'Click a row to open its details and contextual actions.',
            activeTab === 'invites' || activeTab === 'requests'
              ? 'Tick a checkbox only when you want to use the selection action bar. It does not open the record.'
              : 'This tab uses row details rather than checkbox selection.',
            'Squady does not execute withdrawal or decline as a quick action; those choices always remain behind a confirmation.'
          ],
          anchorIds: ['tournaments-results', 'tournaments-selection']
        }
      ]
    };
  }

  getDaysLeftText(value?: string): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    const days = Math.round((Number(target) - Number(today)) / 86400000);
    if (days < 0) return 'Overdue';
    if (days === 0) return 'Due today';
    return `${days} day${days === 1 ? '' : 's'} left`;
  }
  isDeadlineSoon(value?: string): boolean {
    const text = this.getDaysLeftText(value);
    if (text === 'Overdue' || text === 'Due today') return true;
    const days = Number(text.split(' ')[0]);
    return Number.isFinite(days) && days <= 4;
  }

  renderIcon(name: string, size = 16): React.ReactNode {
    const paths = {
      search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3',
      columns: 'M3 4h18M8 4v16M16 4v16M3 20h18',
      trophy: 'M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0zM7 4H4v2a3 3 0 0 0 3 3M17 4h3v2a3 3 0 0 1-3 3',
      mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2M22 7l-10 6L2 7',
      check: 'M20 6 9 17l-5-5',
      hourglass: 'M7 3h10M7 21h10M8 3v4c0 2 4 3 4 5s-4 3-4 5v4M16 3v4c0 2-4 3-4 5s4 3 4 5v4',
      warning: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
      withdraw: 'M10 17l5-5-5-5M15 12H3M14 4h5a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5',
      calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
      teams:
        'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
      events:
        'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM8 14h3M8 18h6',
      document: 'M6 2h8l4 4v16H6zM14 2v5h5M9 12h6M9 16h6',
      external: 'M14 3h7v7M21 3l-10 10M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'
    };
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={paths[name] || paths.trophy} />
      </svg>
    );
  }
  getTabs(): { key: TabKey; label: string; subtitle: string }[] {
    return [
      { key: 'upcoming', label: 'Upcoming', subtitle: 'Tournaments your school is entered or confirmed to play in.' },
      { key: 'waiting', label: 'Waiting list', subtitle: 'Tournaments where your entry is queued for a place.' },
      { key: 'invites', label: 'Invites', subtitle: 'Invitations from organisers to join a tournament.' },
      { key: 'requests', label: 'Requests', subtitle: 'Confirm or withdraw participation for booked tournaments.' },
      { key: 'archive', label: 'Archive', subtitle: 'Past tournaments and resolved invites and confirmation requests.' }
    ];
  }
  renderTabs(): React.ReactNode {
    return (
      <div className="tnTabs" role="tablist" aria-label="Tournament sections" data-help-anchor="tournaments-tabs">
        {this.getTabs().map(tab => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={this.state.activeTab === tab.key}
            className={this.state.activeTab === tab.key ? 'isActive' : ''}
            onClick={() => this.switchTab(tab.key)}
          >
            {tab.label}
            <span>{this.state.counts[tab.key]}</span>
          </button>
        ))}
        <button type="button" role="tab" aria-selected={false} onClick={() => this.props.history.push('/tournaments/chats')}>
          Chats
          {this.state.chatUnreadCount ? <span className="tnTabs__unread">{this.state.chatUnreadCount}</span> : null}
        </button>
        <button type="button" role="tab" aria-selected={false} onClick={() => this.props.history.push('/tournamentAvailability')}>Union representation</button>
      </div>
    );
  }

  renderSummaryStrip(): React.ReactNode {
    const { counts, nextTournament } = this.state;
    const nextDate = nextTournament ? this.formatDate(nextTournament.startTime) : 'No date scheduled';

    const items: Array<{
      key: string;
      tab: TabKey;
      label: string;
      value: React.ReactNode;
      tone: string;
      title?: string;
    }> = [
      {
        key: 'next',
        tab: 'upcoming',
        label: 'Next tournament',
        value: nextDate,
        tone: 'date',
        title: nextTournament ? nextTournament.name : undefined
      },
      {
        key: 'upcoming',
        tab: 'upcoming',
        label: 'Upcoming',
        value: counts.upcoming,
        tone: 'upcoming'
      },
      {
        key: 'invites',
        tab: 'invites',
        label: 'Pending invites',
        value: counts.invites,
        tone: 'invites'
      },
      {
        key: 'requests',
        tab: 'requests',
        label: 'Awaiting confirmation',
        value: counts.requests,
        tone: 'requests'
      },
      {
        key: 'waiting',
        tab: 'waiting',
        label: 'Waiting list',
        value: counts.waiting,
        tone: 'waiting'
      }
    ];

    return (
      <section className="tnSummaryStrip" aria-label="Tournament overview" data-help-anchor="tournaments-summary">
        {items.map(item => (
          <button
            key={item.key}
            type="button"
            className={`tnSummaryChip is-${item.tone}`}
            onClick={() => this.switchTab(item.tab)}
            title={item.title}
          >
            <span className="tnSummaryChip__copy">
              <small>{item.label}</small>
              <strong>{item.value}</strong>
            </span>
          </button>
        ))}
      </section>
    );
  }
  getTabTitle(): string {
    switch (this.state.activeTab) {
      case 'waiting':
        return 'Waiting list';
      case 'invites':
        return 'Tournament invites';
      case 'requests':
        return 'Confirmation requests';
      case 'archive':
        return 'Archive';
      default:
        return 'Upcoming tournaments';
    }
  }

  getColumns(): Col[] {
    const inbox: Col[] = [
      { key: 'received', header: 'Received', sortable: true },
      { key: 'name', header: 'Tournament', sortable: true, locked: true },
      { key: 'ages', header: 'Age / gender' },
      { key: 'date', header: 'Starts', sortable: true },
      { key: 'deadline', header: 'Respond by', sortable: true },
      { key: 'status', header: 'Status' }
    ];
    switch (this.state.activeTab) {
      case 'waiting':
        return [
          { key: 'date', header: 'Date', sortable: true },
          { key: 'name', header: 'Tournament', sortable: true, locked: true },
          { key: 'ages', header: 'Age / gender' },
          { key: 'venue', header: 'Venue' },
          { key: 'queue', header: 'Queue position', sortable: true }
        ];
      case 'invites':
      case 'requests':
        return inbox;
      case 'archive':
        return [
          { key: 'date', header: 'Date', sortable: true },
          { key: 'name', header: 'Tournament', sortable: true, locked: true },
          { key: 'kind', header: 'Kind' },
          { key: 'status', header: 'Status' }
        ];
      default:
        return [
          { key: 'date', header: 'Date', sortable: true },
          { key: 'name', header: 'Tournament', sortable: true, locked: true },
          { key: 'ages', header: 'Age / gender' },
          { key: 'venue', header: 'Venue' },
          { key: 'status', header: 'Status' }
        ];
    }
  }
  getVisibleColumns(): Col[] {
    return this.getColumns().filter(column => column.locked || !this.state.hiddenColumns.includes(column.key));
  }
  toggleColumn(key: ColKey): void {
    const column = this.getColumns().find(item => item.key === key);
    if (!column || column.locked) return;
    this.setState(state => {
      const hiddenColumns = state.hiddenColumns.includes(key)
        ? state.hiddenColumns.filter(item => item !== key)
        : [...state.hiddenColumns, key];
      saveHiddenColumns(this.props.user, getColumnsViewKey(state.activeTab), hiddenColumns);
      return { hiddenColumns };
    });
  }
  resetColumns = (): void => {
    saveHiddenColumns(this.props.user, getColumnsViewKey(this.state.activeTab), []);
    this.setState({ hiddenColumns: [] });
  };
  isFilterActive(): boolean {
    const { search, sportFilter, organiserFilter, statusFilter, seasonFilter, dateFrom, dateTo } = this.state;
    return Boolean(search || sportFilter || organiserFilter || statusFilter || seasonFilter || dateFrom || dateTo);
  }
  getArchiveCounts(): Record<ArchiveKind, number> {
    return this.state.archiveCounts;
  }

  renderToolbar(): React.ReactNode {
    const sourceRows = this.getSourceRows();
    const sports = Array.from(new Set(sourceRows.map(row => row.sport).filter(Boolean))).sort();
    const organisers = Array.from(new Set(sourceRows.map(row => row.organiser).filter(Boolean))).sort();
    const statuses = Array.from(new Set(sourceRows.map(row => row.status).filter(Boolean))).sort();
    const seasons = Array.from(
      new Set(sourceRows.map(row => this.getSeasonFromDate(row.startTime)).filter(Boolean))
    ).sort((a, b) => Number(b) - Number(a));
    const isPendingTab = this.state.activeTab === 'invites' || this.state.activeTab === 'requests';
    const pendingResponses = isPendingTab ? this.state.counts[this.state.activeTab] : 0;
    return (
      <div className="tnToolbar">
        <div className="tnToolbarHead">
          <div className="tnToolbarMain">
            <div className="tnSectionTitleRow">
              <h2>{this.getTabTitle()}</h2>
              <span className="tnListCount">{this.state.counts[this.state.activeTab].toLocaleString()}</span>
              {pendingResponses > 0 ? (
                <span
                  className="tnResponseBadge"
                  title={
                    this.state.activeTab === 'invites'
                      ? 'Tournament invites awaiting response'
                      : 'Tournament confirmation requests awaiting response'
                  }
                >
                  {this.renderIcon('mail', 15)}
                  {pendingResponses}
                </span>
              ) : null}
              <label className="tnSearch" data-help-anchor="tournaments-search">
                <input
                  value={this.state.searchDraft}
                  placeholder="Search tournaments..."
                  onChange={event => this.setState({ searchDraft: event.target.value })}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      this.setState({
                        search: this.state.searchDraft,
                        currentPage: 1,
                        selectedIds: [],
                        previewId: null
                      });
                    }
                  }}
                />
                {this.state.searchDraft ? (
                  <button type="button" onClick={() => this.clearFilter('search')} aria-label="Clear search">
                    ×
                  </button>
                ) : null}
              </label>
              <button
                type="button"
                className={this.state.isFilterPanelOpen ? 'tnIconBtn isOn' : 'tnIconBtn'}
                data-help-anchor="tournaments-filters"
                onClick={() => (this.state.isFilterPanelOpen ? this.cancelFilters() : this.openFilters())}
                aria-label="Filters"
                aria-expanded={this.state.isFilterPanelOpen}
              >
                <span className="tnMaskedIcon tnMaskedIcon--filter" />
                Filters
                {this.isFilterActive() ? (
                  <b>
                    {
                      [
                        this.state.seasonFilter,
                        this.state.dateFrom || this.state.dateTo,
                        this.state.sportFilter,
                        this.state.organiserFilter,
                        this.state.statusFilter
                      ].filter(Boolean).length
                    }
                  </b>
                ) : null}
              </button>
              {this.renderColumnsMenu()}
              <button
                type="button"
                className="tnIconBtn"
                onClick={() => this.setState({ reportModal: 'schedule' })}
              >
                {this.renderIcon('document', 16)}
                Reports
              </button>
            </div>
          </div>
        </div>
        {this.state.isFilterPanelOpen ? (
          <div
            className="tnFilterPanel"
            aria-label="Tournament filters"
            ref={this.filterPanelRef}
            onKeyDown={event => {
              if (event.key === 'Enter' && (event.target as HTMLElement).tagName !== 'BUTTON') {
                event.preventDefault();
                this.applyFilters();
              }
            }}
          >
            <header>
              <strong>Filters</strong>
              <button type="button" onClick={this.cancelFilters} aria-label="Close filters">
                ×
              </button>
            </header>
            <div className="tnFilterPanel__body">
              <label>
                <span>Season</span>
                <select
                  value={this.state.seasonFilter}
                  onChange={event => this.updateFilter('seasonFilter', event.target.value)}
                >
                  <option value="">All seasons</option>
                  {seasons.map(season => (
                    <option key={season} value={season}>
                      {season}/{String(Number(season) + 1).slice(-2)}
                    </option>
                  ))}
                </select>
              </label>
              <DatePicker
                label="Tournament date"
                mode="range"
                presets
                size="sm"
                numberOfMonths={2}
                value={{
                  start: this.parseFilterDate(this.state.dateFrom),
                  end: this.parseFilterDate(this.state.dateTo)
                }}
                onChange={this.onTournamentDateRangeChange}
                placeholder="Any date"
                className="tnDateRangePicker"
              />
              <SearchMultiSelectFilter
                label="Sport"
                selected={this.state.sportFilter ? [this.state.sportFilter] : []}
                options={sports.map(sport => ({ value: sport, label: sport }))}
                onToggle={sport => this.updateFilter('sportFilter', this.state.sportFilter === sport ? '' : sport)}
                placeholder="Search sports…"
                emptyText="No matching sports"
              />
              <label>
                <span>Organising union</span>
                <select
                  value={this.state.organiserFilter}
                  onChange={event => this.updateFilter('organiserFilter', event.target.value)}
                >
                  <option value="">All organising unions</option>
                  {organisers.map(organiser => (
                    <option key={organiser} value={organiser}>
                      {organiser}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="tnFilterChips">
                <legend>Status</legend>
                <div>
                  {statuses.map(status => (
                    <button
                      key={status}
                      type="button"
                      className={this.state.statusFilter === status ? 'isSelected' : ''}
                      onClick={() =>
                        this.updateFilter('statusFilter', this.state.statusFilter === status ? '' : status)
                      }
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </fieldset>
            </div>
            <footer>
              <button type="button" className="tnFilterClear" onClick={this.clearFilters}>
                Clear all
              </button>
              <div>
                <button type="button" className="tnBtn tnBtnGhost" onClick={this.cancelFilters}>
                  Cancel
                </button>
                <button type="button" className="tnBtn tnBtnPrimary" onClick={this.applyFilters}>
                  Apply
                </button>
              </div>
            </footer>
          </div>
        ) : null}
      </div>
    );
  }
  renderColumnsMenu(): React.ReactNode {
    const columns = this.getColumns(),
      visibleCount = this.getVisibleColumns().length;
    return (
      <div className="tnColumns" data-tn-columns-menu="true" data-help-anchor="tournaments-columns">
        <button
          type="button"
          className={this.state.isColumnsMenuOpen ? 'tnColumnsBtn isOpen' : 'tnColumnsBtn'}
          onClick={() => this.setState(state => ({ isColumnsMenuOpen: !state.isColumnsMenuOpen }))}
          aria-expanded={this.state.isColumnsMenuOpen}
        >
          <span className="tnMaskedIcon tnMaskedIcon--columns" />
          Columns<span>{visibleCount}</span>
        </button>
        {this.state.isColumnsMenuOpen && (
          <div className="tnColumnsMenu">
            <div className="tnColumnsMenuHead">
              <strong>Show / hide columns</strong>
              <button type="button" onClick={this.resetColumns}>
                Reset
              </button>
            </div>
            {columns.map(column => {
              const visible = !this.state.hiddenColumns.includes(column.key);
              return (
                <button
                  key={column.key}
                  type="button"
                  className={column.locked ? 'tnColumnOption isLocked' : 'tnColumnOption'}
                  disabled={column.locked}
                  onClick={() => this.toggleColumn(column.key)}
                >
                  <span className={visible ? 'tnColumnCheck isChecked' : 'tnColumnCheck'}>{visible ? '✓' : ''}</span>
                  {column.header}
                  {column.locked ? <em>Locked</em> : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  renderArchiveSegment(): React.ReactNode {
    if (this.state.activeTab !== 'archive') return null;
    const counts = this.getArchiveCounts(),
      options: { id: ArchiveKind; label: string }[] = [
        { id: 'all', label: 'All' },
        { id: 'tournament', label: 'Tournaments' },
        { id: 'invite', label: 'Invites' },
        { id: 'request', label: 'Requests' }
      ];
    return (
      <div className="tnArchiveSegment" data-help-anchor="tournaments-archive-kinds">
        {options.map(option => (
          <button
            key={option.id}
            type="button"
            className={this.state.archiveKind === option.id ? 'isActive' : ''}
            onClick={() => this.setState({ archiveKind: option.id, selectedIds: [], currentPage: 1, previewId: null })}
          >
            {option.label}
            <span>{counts[option.id]}</span>
          </button>
        ))}
      </div>
    );
  }

  toggleRow(row: Row, checked: boolean): void {
    this.setState(state => ({
      selectedIds: checked ? [...state.selectedIds, row.id] : state.selectedIds.filter(id => id !== row.id)
    }));
  }
  renderTable(): React.ReactNode {
    const rows = this.getActiveRows(),
      columns = this.getVisibleColumns(),
      selected = new Set(this.state.selectedIds),
      isPending = this.state.activeTab === 'invites' || this.state.activeTab === 'requests',
      allSelected = rows.length > 0 && rows.every(row => selected.has(row.id));
    const hasActiveCriteria =
      this.getPageHelpFilters().length > 0 || (this.state.activeTab === 'archive' && this.state.archiveKind !== 'all');
    return (
      <div className="tnTableCard" data-help-anchor="tournaments-results">
        <div className="tnTableScroll">
          <table className="tnTable">
            <thead>
              <tr>
                {isPending && (
                  <th className="tnCheck">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={event =>
                        this.setState({ selectedIds: event.target.checked ? rows.map(row => row.id) : [] })
                      }
                    />
                  </th>
                )}
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={column.sortable ? 'isSortable' : ''}
                    onClick={column.sortable ? () => this.onSort(column.key) : undefined}
                  >
                    <span>
                      {column.header}
                      {column.sortable ? this.renderSortIcon(column.key) : null}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + Number(isPending)} className="tnEmpty">
                    <ListEmptyState
                      icon={this.getEmptyIcon(this.state.activeTab)}
                      title={
                        hasActiveCriteria
                          ? `No ${this.getTabTitle().toLowerCase()} match your filters`
                          : this.getEmptyTitle(this.state.activeTab)
                      }
                      description={
                        hasActiveCriteria
                          ? 'Try changing or clearing the search and filters to see more records.'
                          : this.getEmptyHelpText(this.state.activeTab)
                      }
                    />
                  </td>
                </tr>
              ) : (
                rows.map(row => (
                  <tr
                    key={row.id}
                    className={this.state.previewId === row.id ? 'isPreview' : ''}
                    onClick={() => this.openPreview(row)}
                  >
                    {isPending && (
                      <td className="tnCheck" onClick={event => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(row.id)}
                          onChange={event => this.toggleRow(row, event.target.checked)}
                        />
                      </td>
                    )}
                    {columns.map(column => (
                      <td key={column.key}>{this.renderCell(row, column.key)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  renderPagination(): React.ReactNode {
    const total = this.getFilteredRows().length;
    const { currentPage, pageSize } = this.state;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (totalPages <= 1) return null;

    return (
      <nav className="tnPagination" aria-label="Tournaments pagination" data-help-anchor="tournaments-pagination">
        <button
          type="button"
          className="tnPageBtn"
          disabled={currentPage === 1}
          onClick={() => this.setState({ currentPage: Math.max(1, currentPage - 1), selectedIds: [] })}
        >
          Previous
        </button>
        <span className="tnPagination__info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          className="tnPageBtn"
          disabled={currentPage === totalPages}
          onClick={() => this.setState({ currentPage: Math.min(totalPages, currentPage + 1), selectedIds: [] })}
        >
          Next
        </button>
      </nav>
    );
  }
  renderSortIcon(key: ColKey): React.ReactNode {
    const active = this.state.sortKey === key;
    return <em className={active ? `tnSort is${this.state.sortDir}` : 'tnSort'} />;
  }
  renderCell(row: Row, key: ColKey): React.ReactNode {
    switch (key) {
      case 'name': {
        const tournamentType =
          (TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING as any)[row.tournamentType] || row.tournamentType || 'Type not set';
        return (
          <div className="tnName">
            <div className="tnName__title">
              <strong>{row.name}</strong>
              {row.publicSiteUrl ? (
                <a
                  href={row.publicSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tnPublicSiteIcon"
                  title="Open tournament public website"
                  aria-label={`Open public website for ${row.name}`}
                  onClick={event => event.stopPropagation()}
                >
                  {this.renderIcon('external', 14)}
                </a>
              ) : null}
            </div>
            <div className="tnName__meta">
              <i style={{ backgroundColor: this.getSportColor(row.sport) }} />
              <span>{row.sport || 'Sport not set'}</span>
              <b>·</b>
              <span>{tournamentType}</span>
            </div>
            {row.organiser || row.venue ? <small>{row.organiser || row.venue}</small> : null}
          </div>
        );
      }
      case 'sport':
        return row.sport || '-';
      case 'date':
        return <span className="tnDate">{this.formatDate(row.startTime)}</span>;
      case 'received':
        return <span className="tnDate">{this.formatDate(row.receivedAt)}</span>;
      case 'deadline':
        return (
          <div className={this.isDeadlineSoon(row.deadline) ? 'tnDeadline isSoon' : 'tnDeadline'}>
            <strong>{this.formatDate(row.deadline)}</strong>
            {this.getDaysLeftText(row.deadline) ? <small>{this.getDaysLeftText(row.deadline)}</small> : null}
          </div>
        );
      case 'ages':
        return this.getAgeGenderLabel(row);
      case 'venue':
        return <span className="tnVenue">{row.venue || '-'}</span>;
      case 'queue':
        return <span className="tnQueue">#{row.waitPosition || '-'}</span>;
      case 'kind':
        return (
          <span className={`tnKindPill tnKindPill--${row.kind}`}>
            {row.kind === 'invite' ? 'Invite' : row.kind === 'request' ? 'Request' : 'Tournament'}
          </span>
        );
      case 'status':
        return <span className={`tnStatus tnStatus--${row.tone}`}>{row.status}</span>;
      default:
        return null;
    }
  }
  renderSelectionBar(): React.ReactNode {
    const count = this.state.selectedIds.length;
    if (count === 0 || (this.state.activeTab !== 'invites' && this.state.activeTab !== 'requests')) return null;
    return (
      <div className="tnSelection" data-help-anchor="tournaments-selection">
        <strong>{count} selected</strong>
        <div>
          <button
            type="button"
            className="tnBtn tnBtnPrimary"
            disabled={this.state.activeTab === 'invites' && count !== 1}
            onClick={() => {
              if (this.state.activeTab === 'invites' && count === 1) {
                this.setState({ previewId: this.state.selectedIds[0] });
              } else {
                this.openLegacyAction();
              }
            }}
          >
            {this.state.activeTab === 'invites' ? 'Review invite' : 'Review requests'}
          </button>
          <button type="button" className="tnBtn tnBtnGhost" onClick={() => this.setState({ selectedIds: [] })}>
            Clear
          </button>
        </div>
      </div>
    );
  }
  openLegacyAction = (row?: Row) => {
    const kind = row ? row.kind : this.state.activeTab === 'requests' ? 'request' : 'invite';
    if (kind === 'invite') {
      const selectedRow =
        row ||
        (this.state.selectedIds.length === 1
          ? this.state.rows.invites.find(inviteRow => inviteRow.id === this.state.selectedIds[0])
          : undefined);
      if (selectedRow) {
        this.props.history.push({
          pathname: '/tournaments/invites/manage',
          state: { reviewInvite: selectedRow.raw }
        });
      } else {
        this.props.history.push('/tournaments/invites/manage');
      }
    }
    if (kind === 'request') this.props.history.push('/messages/tournamentConfirmationRequests/inbox');
  };
  getTournamentId(row: Row): string {
    return String(
      propz.get(
        row,
        ['raw', 'id'],
        propz.get(row, ['raw', 'tournamentId'], propz.get(row, ['raw', 'tournament', 'id'], ''))
      ) || ''
    );
  }
  openTournamentRoute = (row: Row, pathname: string) => {
    const tournamentId = this.getTournamentId(row);
    if (!tournamentId) return;
    this.props.history.push({
      pathname,
      search: `tournamentId=${tournamentId}`,
      state: {
        search: this.props.location.search,
        isGoBackWithoutSave: true,
        prevRoutePath: this.props.location.pathname,
        tournamentsHubState: {
          archiveKind: this.state.archiveKind,
          search: this.state.search,
          searchDraft: this.state.searchDraft,
          sportFilter: this.state.sportFilter,
          organiserFilter: this.state.organiserFilter,
          statusFilter: this.state.statusFilter,
          dateFrom: this.state.dateFrom,
          dateTo: this.state.dateTo,
          seasonFilter: this.state.seasonFilter,
          sortKey: this.state.sortKey,
          sortDir: this.state.sortDir,
          currentPage: this.state.currentPage,
          pageSize: this.state.pageSize
        }
      }
    });
  };
  openTournament = (row: Row) => this.openTournamentRoute(row, '/tournaments/events');
  openTeams = (row: Row) => this.openTournamentRoute(row, '/tournaments/teams');
  openTeamPlayersFromHelp = (row: Row, mode: 'manage' | 'allocate') => {
    const tournamentId = this.getTournamentId(row);
    const participation = this.state.previewParticipation;
    if (!tournamentId || !participation || !participation.firstTeamId) {
      this.openTeams(row);
      return;
    }
    this.props.history.push({
      pathname: mode === 'manage' ? '/tournaments/teamPlayers/manage' : '/tournaments/teamPlayers',
      search: `teamId=${encodeURIComponent(participation.firstTeamId)}&tournamentId=${encodeURIComponent(
        tournamentId
      )}`,
      state: {
        search: this.props.location.search,
        teamsSearch: this.props.location.search,
        teamName: participation.firstTeamName,
        tournamentId,
        prevRoutePath: this.props.location.pathname,
        isGoBackWithoutSave: true
      }
    });
  };
  getActionRow(): Row | undefined {
    const { actionRowId } = this.state;
    if (!actionRowId) return undefined;
    const allRows = Object.keys(this.state.rows).reduce((acc, key) => acc.concat(this.state.rows[key]), [] as Row[]);
    return allRows.find(row => row.id === actionRowId);
  }
  openWithdrawTournament = (row: Row) => {
    const { user } = this.props;
    const tournamentId = this.getTournamentId(row);
    if (!tournamentId) return;
    const filter = {
      where: {
        invitedSchoolIds: {
          $in: [user.activeSchoolId]
        }
      },
      limit: 1,
      skip: DEFAULT_SKIP
    };

    this.setState({ isActionLoading: true, actionError: '', actionRowId: row.id });
    getTournamentEvents(user, tournamentId, filter)
      .then(events => {
        this.setState({
          isActionLoading: false,
          actionKind: events.length > 0 ? 'blockedWithdraw' : 'withdrawTournament'
        });
      })
      .catch(() => {
        this.setState({
          isActionLoading: false,
          actionKind: 'error',
          actionError: 'Could not check tournament events. Please try again.'
        });
      });
  };
  openWithdrawWaiting = (row: Row) => {
    this.setState({ actionKind: 'withdrawWaiting', actionRowId: row.id, actionError: '', actionMessage: '' });
  };
  openAcceptInvite = (row: Row) => {
    const tournamentId = String(
      propz.get(row, ['raw', 'tournament', 'id'], propz.get(row, ['raw', 'tournamentId'], '')) || ''
    );
    if (!tournamentId) return;
    this.props.history.push({
      pathname: '/tournament/invite',
      search: `tournament=${encodeURIComponent(tournamentId)}`,
      state: { returnTo: '/tournaments/invites' }
    });
  };
  openDeclineInvite = (row: Row) => {
    this.setState({ actionKind: 'declineInvite', actionRowId: row.id, actionError: '', actionMessage: '' });
  };
  openAcceptRequest = (row: Row) => {
    this.setState({
      confirmationRequest: row.raw,
      actionRowId: row.id,
      actionError: '',
      isConfirmationRequestDirty: false,
      isDiscardConfirmationOpen: false
    });
  };
  closeAcceptRequest = () => {
    if (this.state.isActionLoading) return;
    if (this.state.isConfirmationRequestDirty) {
      this.setState({ isDiscardConfirmationOpen: true });
      return;
    }
    this.discardConfirmationRequest();
  };
  discardConfirmationRequest = () => {
    this.setState({
      confirmationRequest: null,
      actionError: '',
      isConfirmationRequestDirty: false,
      isDiscardConfirmationOpen: false
    });
  };
  openDeclineRequest = (row: Row) => {
    this.setState({ actionKind: 'declineRequest', actionRowId: row.id, actionError: '', actionMessage: '' });
  };
  submitAcceptRequest = (fields: any[], submission?: FormSubmission) => {
    const request = this.state.confirmationRequest;
    if (!request) return;
    this.setState({ isActionLoading: true, actionError: '' });
    acceptConfirmationRequest(this.props.user, request.id, { fields, ...submission })
      .then(() => {
        this.setState(
          {
            isActionLoading: false,
            confirmationRequest: null,
            isConfirmationRequestDirty: false,
            isDiscardConfirmationOpen: false,
            actionKind: 'success',
            actionMessage: 'Your tournament participation has been confirmed.',
            selectedIds: [],
            previewId: null
          },
          this.loadData
        );
      })
      .catch(() => this.setState({ isActionLoading: false, actionError: 'The confirmation could not be sent.' }));
  };
  closeActionModal = () => {
    if (this.state.isActionLoading) return;
    this.setState({ actionKind: null, actionRowId: null, actionMessage: '', actionError: '' });
  };
  confirmAction = () => {
    const row = this.getActionRow();
    if (!row) return;
    const action = this.state.actionKind;
    if (action === 'declineRequest') {
      const requestId = String(propz.get(row, ['raw', 'id'], '') || '');
      if (!requestId) return;
      this.setState({ isActionLoading: true, actionError: '' });
      rejectConfirmationRequest(this.props.user, requestId)
        .then(() => {
          this.setState(
            {
              isActionLoading: false,
              actionKind: 'success',
              actionMessage:
                'The confirmation request has been declined. The organiser will assume your school is no longer participating.',
              selectedIds: [],
              previewId: null
            },
            this.loadData
          );
        })
        .catch(() => this.setState({ isActionLoading: false, actionError: 'The request could not be declined.' }));
      return;
    }
    if (action === 'declineInvite') {
      const inviteId = String(propz.get(row, ['raw', 'id'], '') || '');
      if (!inviteId) return;
      this.setState({ isActionLoading: true, actionError: '' });
      declineAdminSchoolsInvites(this.props.user, inviteId)
        .then(() => {
          this.setState(
            {
              isActionLoading: false,
              actionKind: 'success',
              actionMessage: 'The tournament invite has been declined.',
              selectedIds: [],
              previewId: null
            },
            this.loadData
          );
        })
        .catch(() => this.setState({ isActionLoading: false, actionError: 'The invite could not be declined.' }));
      return;
    }
    const tournamentId = this.getTournamentId(row);
    if (!tournamentId) return;

    const { user } = this.props;
    const request = action === 'withdrawWaiting' ? withdrawTournamentBooking : unsubscribeTournament;

    this.setState({ isActionLoading: true, actionError: '' });
    request(user, tournamentId)
      .then(() => {
        this.setState(
          {
            isActionLoading: false,
            actionKind: 'success',
            actionMessage:
              action === 'withdrawWaiting'
                ? 'Your school has been removed from the waiting list.'
                : 'Your school has been withdrawn from the tournament.',
            selectedIds: [],
            previewId: null
          },
          this.loadData
        );
      })
      .catch(() => {
        this.setState({ isActionLoading: false, actionError: 'The action could not be completed. Please try again.' });
      });
  };
  renderDrawerActions(row: Row): React.ReactNode {
    const isArchive = this.state.activeTab === 'archive';
    if (row.kind === 'invite') {
      if (isArchive || row.status === STATUS_META.OUTDATED.label) return null;
      return (
        <div className="tnInviteActions">
          <button type="button" className="is-danger" onClick={() => this.openDeclineInvite(row)}>
            Decline
          </button>
          <button type="button" onClick={() => this.openAcceptInvite(row)}>
            Accept
          </button>
        </div>
      );
    }
    if (row.kind === 'request') {
      if (isArchive) return null;
      return (
        <div className="tnInviteActions">
          <button type="button" className="is-danger" onClick={() => this.openDeclineRequest(row)}>
            Decline
          </button>
          <button type="button" onClick={() => this.openAcceptRequest(row)}>
            Accept
          </button>
        </div>
      );
    }

    if (row.kind === 'waiting') {
      return (
        <button type="button" className="tnBtn tnBtnDanger tnBtnWithIcon" onClick={() => this.openWithdrawWaiting(row)}>
          {this.renderIcon('withdraw', 16)}
          Withdraw from waiting list
        </button>
      );
    }

    const { previewEventsState } = this.state;
    const isEventsDisabled = previewEventsState !== 'available';
    const participation = this.state.previewParticipation;
    const canOpenFirstTeam = Boolean(participation && participation.firstTeamId);
    const showSeparateTeamManagement = Boolean(
      participation && participation.isIndividual && participation.teamsCount > 0 && canOpenFirstTeam
    );
    const teamAction = participation && participation.teamsCount === 0
      ? {
          label: participation.isIndividual ? 'Create tournament team' : 'Add tournament teams',
          run: () => this.openTeams(row)
        }
      : participation && participation.playersCount === 0 && canOpenFirstTeam
      ? { label: 'Add children', run: () => this.openTeamPlayersFromHelp(row, 'manage') }
      : participation && participation.isIndividual && canOpenFirstTeam
      ? { label: 'Children & allocations', run: () => this.openTeamPlayersFromHelp(row, 'allocate') }
      : { label: 'Manage teams', run: () => this.openTeams(row) };
    const eventHelpText =
      previewEventsState === 'loading'
        ? 'Checking for tournament events…'
        : previewEventsState === 'empty'
        ? 'No events have been created for this tournament yet.'
        : previewEventsState === 'error'
        ? 'Events could not be checked. Close and reopen the details to try again.'
        : '';

    return (
      <div className="tnDrawerActions">
        <div className={`tnDrawerActions__main${showSeparateTeamManagement ? ' hasTeamManagement' : ''}`}>
          <button
            type="button"
            className="tnBtn tnBtnPrimary tnBtnWithIcon"
            onClick={() => window.location.assign(`/tournaments/chats?tournamentId=${String(propz.get(row, ['raw', 'id'], '') || row.id).replace(/^tournament:/, '')}`)}
          >
            {this.renderIcon('mail', 16)}
            Message organiser
          </button>
          <button type="button" className="tnBtn tnBtnPrimary tnBtnWithIcon" onClick={teamAction.run}>
            {this.renderIcon('teams', 16)}
            {teamAction.label}
          </button>
          {showSeparateTeamManagement ? (
            <button
              type="button"
              className="tnBtn tnBtnSecondary tnBtnWithIcon tnDrawerActions__manageTeam"
              onClick={() => this.openTeams(row)}
            >
              {this.renderIcon('teams', 16)}
              Manage team
            </button>
          ) : null}
          <button
            type="button"
            className="tnBtn tnBtnSecondary tnBtnWithIcon"
            onClick={() => this.openTournament(row)}
            disabled={isEventsDisabled}
            aria-describedby={eventHelpText ? 'tn-events-help' : undefined}
          >
            {this.renderIcon('events', 16)}
            {participation && participation.isIndividual
              ? 'Review events'
              : 'Manage events'}
          </button>
          <button
            type="button"
            className="tnBtn tnBtnSecondary tnBtnWithIcon"
            onClick={() => this.setState({ reportModal: 'picker' })}
          >
            {this.renderIcon('document', 16)}
            Reports
          </button>
        </div>
        {eventHelpText ? (
          <p id="tn-events-help" className={`tnDrawerActions__help is${previewEventsState}`}>
            {this.renderIcon(previewEventsState === 'error' ? 'warning' : 'calendar', 15)}
            <span>{eventHelpText}</span>
          </p>
        ) : null}
        {!isArchive ? (
          <button
            type="button"
            className="tnBtn tnBtnDanger tnBtnWithIcon tnDrawerActions__withdraw"
            onClick={() => this.openWithdrawTournament(row)}
          >
            {this.renderIcon('withdraw', 16)}
            Withdraw from tournament
          </button>
        ) : null}
      </div>
    );
  }

  closeReportModal = () => this.setState({ reportModal: null });

  onSubmitTournamentReport = (data: any, reportType: string) => {
    const row = this.getPreviewRow();
    const isSchedule = reportType === REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.SCHOOL_TOURNAMENT_SCHEDULE;
    if (!row && !isSchedule) return;
    const { user } = this.props;
    const tournamentId = row ? String(propz.get(row, ['raw', 'id'], '') || row.id).replace(/^tournament:/, '') : '';
    const reportParamsCSV: any = {
      schoolId: user.activeSchoolId,
      tournamentId: tournamentId || undefined
    };

    if (isSchedule) {
      const dateFrom = new Date(data.dateFrom);
      const dateTo = new Date(data.dateTo);
      dateFrom.setHours(0, 0, 0, 0);
      dateTo.setHours(23, 59, 59, 0);
      delete reportParamsCSV.tournamentId;
      Object.assign(reportParamsCSV, {
        dateFrom: Moment(dateFrom).format('YYYY-MM-DD HH:mm:ss'),
        dateTo: Moment(dateTo).format('YYYY-MM-DD HH:mm:ss'),
        showBooked: data.isShowBooked,
        showWaitingList: data.isShowWaitingList,
        showAvailableForBooking: data.isShowAvailableForBooking,
        showCancelledPostponed: data.isShowCancelledPostponed,
        showUnavailableForBooking: data.isShowUnavailableForBooking
      });
    } else if (reportType === REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.TOURNAMENT_SCHOOL_START_LIST) {
      reportParamsCSV.showBlank = data.isShowBlank;
      reportParamsCSV.highlightNoAge = data.isHighlightNoAge;
    } else {
      reportParamsCSV.printFullNames = data.isPrintFullNames;
      reportParamsCSV.printFormName = data.isPrintFormName;
      reportParamsCSV.printYearGroup = data.isPrintYearGroup;
      reportParamsCSV.printGender = data.isPrintGender;
      reportParamsCSV.printDOB = data.isPrintDOB;
    }

    this.setState({ reportModal: null, isLoading: true });
    uploadReport(user, reportType, { reportParamsCSV })
      .then(result => {
        this.setState({ isLoading: false });
        if (!result || !result.reportId) throw new Error('The report could not be generated.');
        window.open(`${window.apiBase}/public/report/${result.reportId}/reportType/${result.reportType}`);
      })
      .catch(error => {
        const message =
          propz.get(error, ['response', 'data', 'details', 'text'], '') ||
          error.message ||
          'The report could not be generated.';
        this.setState({ isLoading: false, actionKind: 'error', actionError: message });
      });
  };

  renderReportModal(): React.ReactNode {
    const { reportModal } = this.state;
    const row = this.getPreviewRow();
    if (!reportModal || (reportModal !== 'schedule' && !row)) return null;

    return (
      <SimpleModal
        isOpen={true}
        onBackdropClick={this.closeReportModal}
        customClass="tnReportDialog"
        showHeader={false}
        showFooter={false}
        dialogStyle={{ maxWidth: reportModal === 'schedule' ? 560 : 520 }}
      >
        {reportModal === 'picker' ? (
          <div className="tnReportPicker">
            <div className="tnReportPicker__heading">
              <span>{this.renderIcon('document', 20)}</span>
              <div><h2>Tournament reports</h2><p>Choose a report for {row!.name}.</p></div>
            </div>
            <button type="button" className="tnBtn tnBtnSecondary" onClick={() => this.setState({ reportModal: 'startList' })}>
              <span className="tnReportPicker__optionIcon">{this.renderIcon('events', 18)}</span>
              <span className="tnReportPicker__optionCopy">
                <strong>School start list</strong>
                <small>Your school’s entries and event allocations.</small>
              </span>
            </button>
            <button type="button" className="tnBtn tnBtnSecondary" onClick={() => this.setState({ reportModal: 'players' })}>
              <span className="tnReportPicker__optionIcon">{this.renderIcon('teams', 18)}</span>
              <span className="tnReportPicker__optionCopy">
                <strong>Tournament players</strong>
                <small>Students included in your tournament teams and events.</small>
              </span>
            </button>
            <div className="tnReportPicker__footer">
              <button type="button" className="tnBtn tnBtnGhost" onClick={this.closeReportModal}>Close</button>
            </div>
          </div>
        ) : reportModal === 'schedule' ? (
          <SchoolTournamentScheduleReportForm
            onCloseClick={this.closeReportModal}
            onSubmit={this.onSubmitTournamentReport}
          />
        ) : reportModal === 'startList' ? (
          <TournamentSchoolStartListReportForm
            onCloseClick={this.closeReportModal}
            onSubmit={this.onSubmitTournamentReport}
            tournament={row!.raw as Tournament}
          />
        ) : (
          <TournamentSchoolPlayersReportForm
            onCloseClick={this.closeReportModal}
            onSubmit={this.onSubmitTournamentReport}
            tournament={row!.raw as Tournament}
          />
        )}
      </SimpleModal>
    );
  }
  renderActionModal(): React.ReactNode {
    const { actionKind, actionMessage, actionError, isActionLoading } = this.state;
    if (!actionKind) return null;
    const row = this.getActionRow();
    const isBlocked = actionKind === 'blockedWithdraw';
    const isSuccess = actionKind === 'success';
    const isError = actionKind === 'error';
    const isInviteSuccess = isSuccess && actionMessage.toLowerCase().includes('invite');
    const isRequestSuccess =
      isSuccess &&
      (actionMessage.toLowerCase().includes('participation') ||
        actionMessage.toLowerCase().includes('confirmation request'));
    const isWaitingSuccess = isSuccess && actionMessage.toLowerCase().includes('waiting list');
    const title = isSuccess
      ? isRequestSuccess
        ? 'Response sent'
        : isInviteSuccess
        ? 'Invite declined'
        : isWaitingSuccess
        ? 'Removed from waiting list'
        : 'Withdrawal complete'
      : isError
      ? 'Action unavailable'
      : isBlocked
      ? 'Withdrawal is blocked'
      : actionKind === 'declineRequest'
      ? 'Decline confirmation request?'
      : actionKind === 'declineInvite'
      ? 'Decline tournament invite?'
      : actionKind === 'withdrawWaiting'
      ? 'Withdraw from waiting list?'
      : 'Withdraw from tournament?';
    const body = isSuccess
      ? actionMessage
      : isError
      ? actionError || 'The action could not be prepared. Please try again.'
      : isBlocked
      ? 'Your school is taking part in one or more events within this tournament. Ask the organiser to remove your school from the related events first, then return here to withdraw.'
      : actionKind === 'declineRequest'
      ? `If you decline, the organiser will assume that your school is no longer taking part in ${
          row ? row.name : 'this tournament'
        }. Your place may be released to another school.`
      : actionKind === 'declineInvite'
      ? `Decline the invitation to ${row ? row.name : 'this tournament'}?`
      : actionKind === 'withdrawWaiting'
      ? `Remove your school from the waiting list for ${row ? row.name : 'this tournament'}?`
      : `Withdraw your school from ${row ? row.name : 'this tournament'}?`;

    return (
      <div className="tnModalBackdrop" role="presentation" onMouseDown={this.closeActionModal}>
        <section
          className={`tnModal ${isSuccess ? 'isSuccess' : isError || isBlocked ? 'isWarning' : 'isDanger'}`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onMouseDown={event => event.stopPropagation()}
        >
          <div className="tnModalHead">
            <span className="tnModalIcon" aria-hidden="true">
              {this.renderIcon(isSuccess ? 'check' : isError || isBlocked ? 'warning' : 'withdraw', 20)}
            </span>
            <div>
              <h3>{title}</h3>
              <p>
                {isSuccess
                  ? 'Your tournament booking status has been updated.'
                  : actionKind === 'declineRequest'
                  ? 'The organiser will be notified that you no longer intend to participate.'
                  : actionKind === 'declineInvite'
                  ? 'This will reject the organiser’s invitation.'
                  : actionKind === 'withdrawWaiting'
                  ? "This will remove your school's queued entry."
                  : isBlocked
                  ? 'The withdrawal cannot be completed yet.'
                  : 'Review this action before continuing.'}
              </p>
            </div>
            <button type="button" aria-label="Close" onClick={this.closeActionModal} disabled={isActionLoading}>
              ×
            </button>
          </div>
          <div className="tnModalBody">
            <div className="tnModalMessage">{body}</div>
            {actionError && !isError ? <div className="tnModalError">{actionError}</div> : null}
          </div>
          <div className="tnModalFoot">
            {isBlocked || isSuccess || isError ? (
              <button type="button" className="tnBtn tnBtnPrimary" onClick={this.closeActionModal}>
                {isSuccess ? 'Done' : 'Close'}
              </button>
            ) : (
              <React.Fragment>
                <button
                  type="button"
                  className="tnBtn tnBtnGhost"
                  onClick={this.closeActionModal}
                  disabled={isActionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tnBtn tnBtnDanger"
                  onClick={this.confirmAction}
                  disabled={isActionLoading}
                >
                  {isActionLoading
                    ? 'Working...'
                    : actionKind === 'declineRequest'
                    ? 'Decline request'
                    : actionKind === 'declineInvite'
                    ? 'Decline invite'
                    : actionKind === 'withdrawWaiting'
                    ? 'Withdraw from waiting list'
                    : 'Confirm withdrawal'}
                </button>
              </React.Fragment>
            )}
          </div>
        </section>
      </div>
    );
  }
  renderDrawer(): React.ReactNode {
    const row = this.getPreviewRow();
    if (!row) return null;
    const isTournamentEntry = row.kind === 'tournament' || row.kind === 'waiting';
    const { drawerTab } = this.state;
    return (
      <React.Fragment>
        <button type="button" className="tnDrawerBackdrop" aria-label="Close" onClick={this.closePreview} />
        <aside
          className={row.kind === 'waiting' ? 'tnDrawer isWaiting' : 'tnDrawer'}
          role="dialog"
          aria-modal="true"
          aria-label="Tournament details"
          data-help-anchor="tournaments-drawer"
        >
          <div className="tnDrawerHead">
            <div className={`tnDrawerIcon tnKind--${row.kind}`}>
              {this.renderIcon(
                row.kind === 'invite'
                  ? 'mail'
                  : row.kind === 'request'
                  ? 'check'
                  : row.kind === 'waiting'
                  ? 'hourglass'
                  : 'trophy',
                22
              )}
            </div>
            <div>
              <h2>{row.name}</h2>
              <p>
                <span>
                  {row.kind === 'invite'
                    ? 'Invite'
                    : row.kind === 'request'
                    ? 'Request'
                    : row.kind === 'waiting'
                    ? 'Waiting list'
                    : 'Tournament'}
                </span>
                <i />
                <span>{this.formatDate(row.startTime)}</span>
              </p>
            </div>
            <button type="button" aria-label="Close" onClick={this.closePreview}>
              ×
            </button>
          </div>
          {isTournamentEntry ? (
            <div className="tnDrawerTabs" role="tablist" aria-label="Tournament information">
              <button
                type="button"
                role="tab"
                aria-selected={drawerTab === 'details'}
                className={drawerTab === 'details' ? 'isActive' : ''}
                onClick={() => this.setState({ drawerTab: 'details' })}
              >
                Tournament details
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={drawerTab === 'updates'}
                className={drawerTab === 'updates' ? 'isActive' : ''}
                onClick={() => this.setState({ drawerTab: 'updates' })}
              >
                Updates &amp; documents
                {Array.isArray(propz.get(row.raw, ['announcements'], [])) &&
                propz.get(row.raw, ['announcements'], []).length > 0 ? (
                  <span>{propz.get(row.raw, ['announcements'], []).length}</span>
                ) : null}
              </button>
            </div>
          ) : null}
          <div className={drawerTab === 'updates' && isTournamentEntry ? 'tnDrawerBody tnDrawerBody--updates' : 'tnDrawerBody'}>
            {drawerTab === 'updates' && isTournamentEntry ? (
              <AdditionalInfo tournament={row.raw as Tournament} />
            ) : (
              <React.Fragment>
            {row.deadline && (
              <div className={this.isDeadlineSoon(row.deadline) ? 'tnDrawerNotice isSoon' : 'tnDrawerNotice'}>
                {this.renderIcon('calendar')}
                <span>
                  Respond by <b>{this.formatDate(row.deadline)}</b>
                  {this.getDaysLeftText(row.deadline) ? `, ${this.getDaysLeftText(row.deadline).toLowerCase()}` : ''}
                </span>
              </div>
            )}
            {row.kind === 'waiting' ? (
              <div className="tnDrawerContext isWaiting">
                <span aria-hidden="true">i</span>
                <div>
                  <strong>Your school is currently on the waiting list.</strong>
                  <p>
                    If a place becomes available, the organiser can move your school into the participant list and you
                    will be notified.
                  </p>
                </div>
              </div>
            ) : row.kind === 'invite' || row.kind === 'request' ? (
              <div className="tnDrawerContext">
                {row.kind === 'invite'
                  ? 'This is an invitation to join a tournament. Accepting books a place for your school.'
                  : 'This is a request to confirm participation for a tournament your school is already booked into.'}
              </div>
            ) : (
              <div className="tnDrawerContext isParticipant">
                <span aria-hidden="true">{this.renderIcon('check', 15)}</span>
                <div>
                  <strong>Your school is entered in this tournament.</strong>
                  <p>Manage your teams here. Tournament events become available once the organiser creates them.</p>
                </div>
              </div>
            )}
            <div className="tnDrawerSectionTitle">
              <span>Tournament details</span>
            </div>
            <div className="tnDrawerDetails">
              {this.renderDrawerField('Status', <span className={`tnStatus tnStatus--${row.tone}`}>{row.status}</span>)}
              {row.waitPosition ? this.renderDrawerField('Queue position', `#${row.waitPosition}`) : null}
              {this.renderDrawerField('Organiser', row.organiser || '-')}
              {this.renderDrawerField('Sport', row.sport || '-')}
              {this.renderDrawerField('Starts', this.formatDate(row.startTime))}
              {this.renderDrawerField('Venue', row.venue || '-')}
              {row.receivedAt ? this.renderDrawerField('Received', this.formatDate(row.receivedAt)) : null}
              {this.renderDrawerField('Age groups & gender', this.getAgeGenderLabel(row))}
            </div>
            {row.publicSiteUrl ? (
              <a href={row.publicSiteUrl} target="_blank" rel="noopener noreferrer" className="tnPublicSiteLink">
                <span className="tnPublicSiteLink__icon" aria-hidden="true">
                  {this.renderIcon('external', 17)}
                </span>
                <span>
                  <small>Tournament website</small>
                  <strong>View the public tournament page</strong>
                </span>
                <b>
                  Open
                  {this.renderIcon('external', 13)}
                </b>
              </a>
            ) : null}
              </React.Fragment>
            )}
          </div>
          <div className="tnDrawerFoot" data-help-anchor="tournaments-drawer-actions">
            {this.renderDrawerActions(row)}
          </div>
        </aside>
      </React.Fragment>
    );
  }
  renderDrawerField(label: string, value: React.ReactNode): React.ReactNode {
    return (
      <div className="tnDrawerField">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    );
  }

  renderConfirmationRequestModal(): React.ReactNode {
    const { confirmationRequest, actionError, isActionLoading } = this.state;
    if (!confirmationRequest) return null;
    return (
      <SimpleModal
        isOpen={true}
        showHeader={false}
        showFooter={false}
        customClass="tnConfirmationRequestDialog"
        dialogStyle={{ maxWidth: 900 }}
        onBackdropClick={this.closeAcceptRequest}
      >
        <section className="tnConfirmationRequestModal" aria-label="Tournament confirmation request">
          <header>
            <div>
              <h2>Tournament confirmation request</h2>
              <p>Review every detail and complete the organiser’s fields before confirming participation.</p>
            </div>
            <button type="button" onClick={this.closeAcceptRequest} disabled={isActionLoading} aria-label="Close">
              ×
            </button>
          </header>
          {actionError ? <div className="tnConfirmationRequestModal__error">{actionError}</div> : null}
          <div className="tnConfirmationRequestModal__body">
            <ConfirmationRequestForm
              user={this.props.user}
              message={confirmationRequest}
              isEditable={true}
              onAcceptClick={this.submitAcceptRequest}
              onCloseClick={this.closeAcceptRequest}
              onDirtyChange={isDirty => this.setState({ isConfirmationRequestDirty: isDirty })}
            />
          </div>
        </section>
      </SimpleModal>
    );
  }

  renderDiscardConfirmationModal(): React.ReactNode {
    if (!this.state.isDiscardConfirmationOpen) return null;
    return (
      <div
        className="tnModalBackdrop tnModalBackdrop--aboveModal"
        role="presentation"
        onMouseDown={() => this.setState({ isDiscardConfirmationOpen: false })}
      >
        <section
          className="tnModal isDanger"
          role="dialog"
          aria-modal="true"
          aria-label="Discard unsaved changes?"
          onMouseDown={event => event.stopPropagation()}
        >
          <div className="tnModalHead">
            <span className="tnModalIcon" aria-hidden="true">
              {this.renderIcon('warning', 20)}
            </span>
            <div>
              <h3>Discard unsaved changes?</h3>
              <p>Your answers have not been submitted.</p>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => this.setState({ isDiscardConfirmationOpen: false })}
            >
              ×
            </button>
          </div>
          <div className="tnModalBody">
            <div className="tnModalMessage">
              You have unsaved changes in this confirmation request. If you close now, your answers will be lost.
            </div>
          </div>
          <div className="tnModalFoot">
            <button
              type="button"
              className="tnBtn tnBtnGhost"
              onClick={() => this.setState({ isDiscardConfirmationOpen: false })}
            >
              Continue editing
            </button>
            <button type="button" className="tnBtn tnBtnDanger" onClick={this.discardConfirmationRequest}>
              Discard changes
            </button>
          </div>
        </section>
      </div>
    );
  }

  render() {
    return (
      <div className="tnPage">
        <header className="tnPageHeader">
          <h1>Tournaments</h1>
          {this.renderTabs()}
        </header>
        {this.renderSummaryStrip()}
        <main className="tnMain">
          <section className="tnListPanel">
            {this.renderToolbar()}
            {this.isFilterActive() ? (
              <div className="tnActiveFilters">
                {this.state.search ? (
                  <span>
                    <b>Search:</b> {this.state.search}
                    <button type="button" onClick={() => this.clearFilter('search')} aria-label="Clear search filter">
                      ×
                    </button>
                  </span>
                ) : null}
                {this.state.seasonFilter ? (
                  <span>
                    <b>Season:</b> {this.state.seasonFilter}
                    <button
                      type="button"
                      onClick={() => this.clearFilter('seasonFilter')}
                      aria-label="Clear season filter"
                    >
                      ×
                    </button>
                  </span>
                ) : null}
                {this.state.dateFrom || this.state.dateTo ? (
                  <span>
                    <b>Date:</b> {[this.state.dateFrom, this.state.dateTo].filter(Boolean).join(' – ')}
                    <button type="button" onClick={() => this.clearFilter('dateRange')} aria-label="Clear date filter">
                      ×
                    </button>
                  </span>
                ) : null}
                {this.state.sportFilter ? (
                  <span>
                    <b>Sport:</b> {this.state.sportFilter}
                    <button
                      type="button"
                      onClick={() => this.clearFilter('sportFilter')}
                      aria-label="Clear sport filter"
                    >
                      ×
                    </button>
                  </span>
                ) : null}
                {this.state.organiserFilter ? (
                  <span>
                    <b>Organising union:</b> {this.state.organiserFilter}
                    <button
                      type="button"
                      onClick={() => this.clearFilter('organiserFilter')}
                      aria-label="Clear organising union filter"
                    >
                      ×
                    </button>
                  </span>
                ) : null}
                {this.state.statusFilter ? (
                  <span>
                    <b>Status:</b> {this.state.statusFilter}
                    <button
                      type="button"
                      onClick={() => this.clearFilter('statusFilter')}
                      aria-label="Clear status filter"
                    >
                      ×
                    </button>
                  </span>
                ) : null}
                <button type="button" className="tnActiveFilters__clear" onClick={this.clearFilters}>
                  Clear all
                </button>
              </div>
            ) : null}
            {this.renderArchiveSegment()}
            {this.renderSelectionBar()}
            {this.state.isLoading ? (
              <Loader />
            ) : (
              <React.Fragment>
                {this.renderTable()}
                {this.renderPagination()}
              </React.Fragment>
            )}
          </section>
        </main>
        {this.renderDrawer()}
        {this.renderConfirmationRequestModal()}
        {this.renderDiscardConfirmationModal()}
        {this.renderReportModal()}
        {this.renderActionModal()}
      </div>
    );
  }
}
